/**
 * Durable appraisal photo upload queue (Phase 15).
 *
 * Replaces the previous "eager upload with a 15s timeout and 3 tries then give
 * up forever" behaviour in StepBasedAppraisal. Photos are written to IndexedDB
 * (transportmate_appraisal_photos) at capture time — that store is the durable
 * queue. This engine drains it in the background:
 *
 *   Camera → blob saved locally + deterministic targetPath
 *          → queue item (status pending_local)
 *          → background upload (AbortController, ~60s)
 *          → exponential backoff, unlimited retries while authenticated
 *          → status "uploaded" → item removed on appraisal completion
 *
 * Guarantees:
 * - Survives screen close, app restart, reboot, force close, signal loss.
 * - Never discards an upload on a transient error.
 * - The destination path is known before upload starts, but it is not exposed
 *   as an uploaded storagePath until Storage and database reconciliation pass.
 */

import { isRecoverableDbError, shouldRunBackgroundStorageWork, resumeStorageRecovery } from "@/lib/offlineDb";
import { supabase } from "@/integrations/supabase/client";
import {
  appraisalPhotoDB,
  materialisePhotoBytes,
  readableRawByteLength,
  DURABLE_READ_TIMEOUT_MS,
  PHOTO_SCHEMA_VERSION,
  type AppraisalPhotoRecord,
} from "@/lib/appraisalPhotoDb";
import { PhotoPipelineError, type PhotoFailureKind, isStorageAuthorizationDenied } from "@/lib/photoFailureKinds";
import { isPhotoDeleted } from "@/lib/appraisalPhotoCancellation";
import { prunePhotoReferences } from "@/lib/appraisalPhotoReferences";
import { drainPhotoReferenceCleanups, enqueuePhotoReferenceCleanup, hasPhotoReferenceCleanups } from "@/lib/appraisalPhotoReferenceCleanupQueue";
import { sha256HexBounded, verifyPayloadIntegrity, verifyStorageLength, integrityFailure, type IntegrityVerdict } from "@/lib/photoIntegrity";
import { captureError, captureMetric } from "@/lib/observability";
import { headPublicPhotoObject } from "@/lib/publicPhotoObjectCheck";
import { isKnownOffline } from "./connectivity";
import { logOfflineEvent } from "./offlineTelemetry";


const BUCKET = "appraisal-photos";
const UPLOAD_TIMEOUT_MS = 60_000;
/** Bound for Storage metadata calls (existence checks). */
const STORAGE_OP_TIMEOUT_MS = 20_000;
const POLL_MS = 15_000;
const MAX_BACKOFF_MS = 5 * 60 * 1000;
/**
 * Terminal recovery policy: a photo may only become an internal support
 * exception after this many *separate* verified failures to materialise its
 * durable bytes, AND only when no object for it exists anywhere in Storage
 * under the appraisal prefix. A single read failure is always retryable.
 */
const DURABLE_READ_TERMINAL_ATTEMPTS = 5;
/**
 * A slow or broken record must not starve the rest of the queue: several
 * photos are drained at once, so one 60s upload timeout delays only itself.
 */
const DRAIN_CONCURRENCY = 3;

export type AppraisalUploadStatus = "idle" | "uploading";

export interface AppraisalUploadSnapshot {
  status: AppraisalUploadStatus;
  pending: number;
}

let status: AppraisalUploadStatus = "idle";
let pending = 0;
let draining = false;
let drainRequested = false;
// 1.0.x — bounded backoff for the 15s poll while local photo storage keeps
// failing with a recoverable IndexedDB error (after withDb's single retry).
// Only the timer respects it; online/foreground/manual/capture drains run now.
const DRAIN_BACKOFF_BASE_MS = 15_000;
const DRAIN_BACKOFF_MAX_MS = 5 * 60_000;
let drainBackoffMs = 0;
let drainBackoffUntil = 0;
export function __getDrainBackoffForTests() { return { drainBackoffMs, drainBackoffUntil }; }
export function __resetDrainBackoffForTests() { drainBackoffMs = 0; drainBackoffUntil = 0; }
let intervalId: ReturnType<typeof setInterval> | null = null;
let cleanup: (() => void) | null = null;

/**
 * One foreground transition → one resume. `visibilitychange` and native
 * `appStateChange` both fire for the same return to foreground; the first
 * one wins. Re-armed by any background signal, or after the window elapses
 * (in case WKWebView never reports the background side).
 */
export const FOREGROUND_RESUME_DEDUPE_MS = 5_000;
export function createForegroundResumeGate(windowMs = FOREGROUND_RESUME_DEDUPE_MS) {
  let lastResumeAt = -Infinity;
  let armed = true;
  return {
    markBackground() { armed = true; },
    shouldResume(now = Date.now()): boolean {
      if (!armed && now - lastResumeAt < windowMs) return false;
      armed = false;
      lastResumeAt = now;
      return true;
    },
  };
}
const listeners = new Set<(snap: AppraisalUploadSnapshot) => void>();
const uploadWaiters = new Map<string, Set<(path: string) => void>>();

// ─── Public API ──────────────────────────────────────────────

export function getAppraisalUploadSnapshot(): AppraisalUploadSnapshot {
  return { status, pending };
}

export function onAppraisalUploadChange(fn: (snap: AppraisalUploadSnapshot) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function notify() {
  const snap = getAppraisalUploadSnapshot();
  for (const fn of listeners) {
    try { fn(snap); } catch { /* ignore */ }
  }
}

/** Deterministic destination path for a captured photo. */
export function buildAppraisalPhotoPath(appraisalId: string, pathLabel: string, createdAt = Date.now()): string {
  const safeLabel = pathLabel.replace(/[^a-zA-Z0-9._-]+/g, "-").replace(/^-+|-+$/g, "") || "photo";
  return `${appraisalId}/${safeLabel}-${createdAt}.jpg`;
}

/**
 * Queue a photo for durable upload. The blob must already be persisted in
 * appraisalPhotoDB (savePhoto) — this only marks it queued and kicks the pump.
 * Returns an explicit queued result. `targetPath` is only a destination;
 * `confirmed` resolves after Storage verification and database reconciliation.
 */
export interface QueuedAppraisalPhoto {
  state: "queued" | "uploaded";
  targetPath: string;
  confirmed: Promise<string>;
}

export async function enqueueAppraisalPhoto(params: {
  appraisalId: string;
  localId: string;
  pathLabel: string;
}): Promise<QueuedAppraisalPhoto> {
  const { appraisalId, localId, pathLabel } = params;
  const existing = await appraisalPhotoDB.getRecord(appraisalId, localId);
  const targetPath = existing?.storagePath
    || existing?.targetPath
    || buildAppraisalPhotoPath(appraisalId, pathLabel, existing?.createdAt);
  if (existing?.status === "uploaded" && existing.dbSyncedAt && existing.storageVerifiedAt) {
    return { state: "uploaded", targetPath, confirmed: Promise.resolve(targetPath) };
  }
  const waiterKey = `${appraisalId}:${localId}`;
  const confirmed = new Promise<string>((resolve) => {
    const waiters = uploadWaiters.get(waiterKey) ?? new Set<(path: string) => void>();
    waiters.add(resolve);
    uploadWaiters.set(waiterKey, waiters);
  });
  await appraisalPhotoDB.patch(appraisalId, localId, {
    targetPath,
    status: existing?.status === "uploading" ? "uploading" : "pending_local",
    attempts: existing?.attempts ?? 0,
    nextRetryAt: existing?.nextRetryAt ?? Date.now(),
    lastError: existing?.lastError ?? null,
    queuedAt: existing?.queuedAt ?? Date.now(),
  });
  captureMetric("appraisal.upload", "queued", 0, {
    appraisalId, localId,
    photoId: `${appraisalId}:${localId}`,
    targetPath,
    schemaVersion: existing?.schemaVersion ?? null,
    category: existing?.category ?? null,
    status: existing?.status ?? "pending_local",
    attempts: existing?.attempts ?? 0,
  });
  void drain();
  return { state: "queued", targetPath, confirmed };
}

/** Pending (not yet uploaded) photos for one appraisal. */
export async function getPendingCountForAppraisal(appraisalId: string): Promise<number> {
  const records = await appraisalPhotoDB.getSummariesForAppraisal(appraisalId).catch(() => []);
  return records.filter(isPendingRecord).length;
}

/**
 * Total photos captured on this device for one appraisal (uploaded + pending).
 * The server uses this as the expected upload count so it can wait for real
 * completeness instead of a fixed delay.
 */
export async function getTotalCountForAppraisal(appraisalId: string): Promise<number> {
  const records = await appraisalPhotoDB.getSummariesForAppraisal(appraisalId).catch(() => []);
  // Photos raised as exceptions cannot reach Storage from this device; counting
  // them would make the server wait for an upload that cannot happen.
  return records.filter((r) => r.status !== "needs_review" && r.status !== "archived_legacy").length;
}

/** Photos raised as internal exceptions — support handles these, not the driver. */
export async function getPhotosNeedingReview(): Promise<PendingAppraisalUploadItem[]> {
  const records = await appraisalPhotoDB.getAllRecordSummaries().catch(() => []);
  return records.filter(isNeedsReviewRecord).map((r) => ({
    id: r.id,
    appraisalId: r.appraisalId,
    localId: r.localId,
    category: r.category,
    status: r.status,
    attempts: r.attempts ?? 0,
    lastError: r.lastError ?? null,
    lastErrorKind: (r as { lastErrorKind?: string | null }).lastErrorKind ?? null,
    createdAt: r.createdAt,
  }));
}



/** Pending photos across every appraisal on this device. */
export async function getPendingUploadCount(): Promise<number> {
  const records = await appraisalPhotoDB.getAllRecordSummaries().catch(() => []);
  return records.filter(isPendingRecord).length;
}

/** Metadata (no blobs held) for every photo still awaiting upload. */
export interface PendingAppraisalUploadItem {
  id: string;
  appraisalId: string;
  localId: string;
  category: string;
  status: string;
  attempts: number;
  lastError: string | null;
  /** Classification of the last failure, used to tell user-actionable from internal. */
  lastErrorKind?: string | null;
  createdAt: number;
}

export async function getPendingUploadItems(): Promise<PendingAppraisalUploadItem[]> {
  const records = await appraisalPhotoDB.getAllRecordSummaries().catch(() => []);
  return records.filter(isPendingRecord).map((r) => ({
    id: r.id,
    appraisalId: r.appraisalId,
    localId: r.localId,
    category: r.category,
    status: r.status,
    attempts: r.attempts ?? 0,
    lastError: r.lastError ?? null,
    lastErrorKind: (r as { lastErrorKind?: string | null }).lastErrorKind ?? null,
    createdAt: r.createdAt,
  }));
}

/**
 * Resolve when every photo for an appraisal has uploaded.
 * Keeps draining in the background; never rejects on transient failures.
 */
export async function waitForAppraisalUploads(
  appraisalId: string,
  opts: { timeoutMs?: number } = {},
): Promise<boolean> {
  const deadline = Date.now() + (opts.timeoutMs ?? 10 * 60 * 1000);
  for (;;) {
    const remaining = await getPendingCountForAppraisal(appraisalId);
    if (remaining === 0) return true;
    if (Date.now() > deadline) return false;
    void drain();
    await sleep(3000);
  }
}

/**
 * Direct upload at appraisal completion (the critical transaction).
 *
 * The background queue remains the durable safety net, but it must not be the
 * mechanism that gets normal appraisal photos to Storage. This drives the real
 * upload work immediately and resolves only when every photo for the appraisal
 * has genuinely uploaded (or the bound/offline exception path is hit, in which
 * case the durable queue keeps ownership exactly as before).
 *
 * It changes no upload, integrity, path or reconciliation behaviour: it only
 * clears retry backoff for this appraisal and drives the existing drain hard.
 */
export interface DirectUploadResult {
  total: number;
  confirmed: number;
  pending: number;
  /** True only when every photo for this appraisal has actually uploaded. */
  complete: boolean;
  durationMs: number;
  reason: "complete" | "offline" | "timeout";
}

export async function uploadAppraisalPhotosNow(
  appraisalId: string,
  opts: { timeoutMs?: number } = {},
): Promise<DirectUploadResult> {
  // Explicit user action ("upload now" / completion): leave any storage pause.
  resumeStorageRecovery("user_upload_now");
  const startedAt = Date.now();
  const deadline = startedAt + (opts.timeoutMs ?? 30_000);

  const summarise = async (reason: DirectUploadResult["reason"]): Promise<DirectUploadResult> => {
    const total = await getTotalCountForAppraisal(appraisalId);
    const pendingNow = await getPendingCountForAppraisal(appraisalId);
    const result: DirectUploadResult = {
      total,
      confirmed: Math.max(0, total - pendingNow),
      pending: pendingNow,
      complete: pendingNow === 0,
      durationMs: Date.now() - startedAt,
      reason: pendingNow === 0 ? "complete" : reason,
    };
    captureMetric("appraisal.upload", "direct_upload_finished", result.durationMs, {
      appraisalId,
      total: result.total,
      confirmed: result.confirmed,
      pending: result.pending,
      reason: result.reason,
    });
    return result;
  };

  if (isKnownOffline()) return summarise("offline");

  captureMetric("appraisal.upload", "direct_upload_started", 0, { appraisalId });

  // Any photo already in retry backoff must be attempted right now — the driver
  // is waiting on this transaction, not on the recovery poll.
  try {
    const records = await appraisalPhotoDB.getSummariesForAppraisal(appraisalId);
    await Promise.all(
      records
        .filter(isPendingRecord)
        .filter((r) => (r.nextRetryAt ?? 0) > Date.now())
        .map((r) => appraisalPhotoDB.patch(r.appraisalId, r.localId, { nextRetryAt: Date.now() })),
    );
  } catch { /* backoff clearing is best-effort */ }

  for (;;) {
    const before = await getPendingCountForAppraisal(appraisalId);
    if (before === 0) return summarise("complete");
    if (isKnownOffline()) return summarise("offline");
    if (Date.now() > deadline) return summarise("timeout");
    // drain() uploads a bounded concurrent batch and returns when it has
    // finished; awaiting it is the actual upload, not a poll.
    await drain();
    // Only yield when a drain made no progress (e.g. another drain was already
    // in flight) — the success path never sleeps.
    if (await getPendingCountForAppraisal(appraisalId) === before) await sleep(400);
  }
}

/** Start the durable uploader (call once on boot). */
export function startAppraisalUploadQueue() {
  if (intervalId) return;
  captureMetric("appraisal.upload", "worker_started", 0);
  void appraisalPhotoDB.getAllRecordSummaries().then((records) => {
    const restored = records.filter(isPendingRecord);
    captureMetric("appraisal.upload", "queue_restored", restored.length, {
      pendingCount: restored.length,
      appraisalCount: new Set(restored.map((record) => record.appraisalId)).size,
    });
  }).catch((err) => captureError("appraisal.upload.restore", err));
  // Launch/resume recovery: reconcile every not-yet-verified record against
  // Storage before draining, so a photo that already landed is repaired rather
  // than re-uploaded, and a record marked uploaded whose object is gone
  // re-enters the queue while its local bytes still exist.
  void archiveBrokenLegacyExceptions()
    .then(() => archiveUnrecoverableExceptions())
    .then(() => archiveOrphanedPendingPhotos())
    .then(() => migrateLegacyRecords())
    .then(() => reconcileAppraisalPhotos())
    .then(() => drain());
  intervalId = setInterval(() => {
    if (Date.now() < drainBackoffUntil) return;
    // 1.0.x — storage recovery failed: probe once per shared backoff window
    // instead of every 15s. Lifecycle/online/user retry resume immediately.
    if (!shouldRunBackgroundStorageWork()) return;
    void drain();
  }, POLL_MS);

  const onOnline = () => { resumeStorageRecovery("online"); void drain(); };
  const foregroundGate = createForegroundResumeGate();
  const onVisible = () => {
    if (document.visibilityState !== "visible") { foregroundGate.markBackground(); return; }
    // Returning from background / iOS suspension / Android process recreation.
    if (!foregroundGate.shouldResume()) return;
    void resumeAppraisalPhotoRecovery(undefined, "visibility");
  };
  window.addEventListener("online", onOnline);
  window.addEventListener("network-status-change", onOnline as EventListener);
  document.addEventListener("visibilitychange", onVisible);

  // H7 — native resume. WKWebView does not always fire `visibilitychange` when
  // an app returns to the foreground, so the existing reconcile+drain is also
  // attached to the Capacitor App lifecycle. No new queue, no new worker, no
  // concurrency change: the same re-entrancy-protected drain() is asked to run
  // again.
  let removeNativeResume: (() => void) | null = null;
  void import("@capacitor/app")
    .then(async ({ App }) => {
      const handle = await App.addListener("appStateChange", ({ isActive }) => {
        if (!isActive) { foregroundGate.markBackground(); return; }
        if (!foregroundGate.shouldResume()) return;
        void resumeAppraisalPhotoRecovery(undefined, "native_resume");
      });
      removeNativeResume = () => { void handle.remove(); };
    })
    .catch(() => { /* web build / plugin unavailable */ });

  cleanup = () => {
    window.removeEventListener("online", onOnline);
    window.removeEventListener("network-status-change", onOnline as EventListener);
    document.removeEventListener("visibilitychange", onVisible);
    removeNativeResume?.();
    removeNativeResume = null;
  };
  console.log("[AppraisalUploadQueue] Started");
}

export function stopAppraisalUploadQueue() {
  if (intervalId) clearInterval(intervalId);
  intervalId = null;
  cleanup?.();
  cleanup = null;
}

/** Manual flush (also used by tests). */
export async function flushAppraisalUploads(): Promise<void> {
  await drain();
}

/**
 * H7 — recovery trigger for "the user came back".
 *
 * Appraisal opened/resumed, app foregrounded, tab made visible: give the
 * EXISTING pipeline another opportunity. This is reconcileAppraisalPhotos()
 * followed by drain() — the same functions the boot path and the 15s poll use,
 * with the same re-entrancy protection (a drain already in flight simply sets
 * `drainRequested`). It creates no second queue, no parallel worker, and
 * changes no concurrency, retry, timeout, path, verification or association
 * behaviour.
 */
export async function resumeAppraisalPhotoRecovery(
  appraisalId?: string,
  trigger: string = "resume",
): Promise<void> {
  // Foreground / appraisal opened: a safe point to leave a storage pause.
  resumeStorageRecovery(trigger);
  try {
    captureMetric("appraisal.upload", "resume_recovery_triggered", 0, {
      appraisalId: appraisalId ?? null, trigger,
    });
    await reconcileAppraisalPhotos(appraisalId);
  } catch (err) {
    captureError("appraisal.upload.resume_recovery", err, {
      severity: "warn",
      metadata: { appraisalId: appraisalId ?? null, trigger },
    });
  }
  await drain();
}

// ─── Internals ───────────────────────────────────────────────

function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}

/**
 * Photo identity for telemetry (1.60.50 — observability only).
 *
 * Every queue/upload event carries enough identity to follow ONE physical photo
 * from capture → durable storage → queue → upload → Storage verification → DB
 * association → completion. Nothing here participates in a pipeline decision;
 * values that genuinely do not exist yet are emitted as null rather than
 * invented, and no new identity is generated.
 */
function photoIdentity(
  record: AppraisalPhotoRecord,
  extra: Record<string, unknown> = {},
): Record<string, unknown> {
  const category = record.category ?? null;
  return {
    appraisalId: record.appraisalId,
    localId: record.localId,
    photoId: record.id ?? `${record.appraisalId}:${record.localId}`,
    targetPath: record.storagePath ?? record.targetPath ?? null,
    schemaVersion: record.schemaVersion ?? null,
    category,
    slotKey: category,
    label: category,
    status: record.status ?? null,
    failureKind: record.lastErrorKind ?? null,
    attempts: record.attempts ?? 0,
    ...extra,
  };
}

/**
 * Run a non-critical state transition without letting its failure break photo
 * uploading, while making the failure observable.
 *
 * Replaces bare `.catch(() => {})` around patch/markUploaded/reconciliation
 * calls: the recovery behaviour is unchanged (the error is still absorbed and
 * the record stays retryable), but it is no longer silent.
 */
async function observeTransition(
  stage: string,
  record: AppraisalPhotoRecord,
  work: Promise<unknown>,
  extra: Record<string, unknown> = {},
): Promise<boolean> {
  try {
    await work;
    return true;
  } catch (error) {
    try {
      logOfflineEvent("photo_state_transition_failed", {
        appraisalId: record.appraisalId,
        store: "appraisal_photos",
        subject: `${record.category}:${record.localId}`,
        stage,
        error,
        ...photoIdentity(record, extra),
      });
      captureError("appraisal.upload.state_transition", error, {
        severity: "warn",
        metadata: photoIdentity(record, { stage, ...extra }),
      });
    } catch { /* telemetry must never break photo uploading */ }
    return false;
  }
}

/**
 * H2 — terminal classification telemetry (observability only).
 *
 * Emitted AFTER the existing code has already classified a record as
 * `needs_review` or `archived_legacy`. It observes that decision; it never
 * makes, widens, narrows or triggers one. Emission is once per photo: the
 * marker is persisted, so repeated boot recovery passes do not re-emit.
 */
async function emitTerminalClassification(
  record: AppraisalPhotoRecord,
  terminalState: "needs_review" | "archived_legacy",
  evidence: Record<string, unknown> = {},
): Promise<void> {
  try {
    const current = await appraisalPhotoDB.getRecord(record.appraisalId, record.localId);
    if (current?.terminalTelemetryAt) return;               // already reported
    const subject = current ?? record;
    if (subject.status !== "needs_review" && subject.status !== "archived_legacy") return;
    const emittedAt = Date.now();
    logOfflineEvent("photo_terminal_classified", {
      ...photoIdentity(subject, {
        terminalState,
        archivedReason: subject.archivedReason ?? null,
        durableReadFailures: subject.durableReadFailures ?? 0,
        emittedAt,
        ...evidence,
      }),
      store: "appraisal_photos",
      subject: `${subject.category}:${subject.localId}`,
    });
    captureMetric("appraisal.upload", "photo_terminal_classified", 1, photoIdentity(subject, {
      terminalState,
      archivedReason: subject.archivedReason ?? null,
      durableReadFailures: subject.durableReadFailures ?? 0,
      ...evidence,
    }));
    await appraisalPhotoDB.patch(record.appraisalId, record.localId, { terminalTelemetryAt: emittedAt });
  } catch { /* telemetry must never break the pipeline */ }
}

/**
 * Hard bound on every network call this queue makes.
 *
 * Production incident (1.58, iOS 18.7): `upload_start` was logged and nothing
 * followed — no `bytes_uploading`, no `storage_response`, no failure. A Storage
 * request issued from WKWebView never settled, so the worker awaited it
 * forever, all DRAIN_CONCURRENCY slots were consumed and no photo in the queue
 * ever uploaded. Photos stayed durable locally, the completion workflow waited
 * for uploads that could not arrive, and previews/PDF/email were all blocked.
 *
 * Every remote call is therefore raced against a timeout. A timeout is a
 * normal, retryable failure: nothing local is discarded.
 */
function withNetworkTimeout<T>(label: string, work: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new PhotoPipelineError("network_upload_failed", `${label} timed out after ${Math.round(ms / 1000)}s`));
    }, ms);
    work.then(
      (value) => { clearTimeout(timer); resolve(value); },
      (err) => { clearTimeout(timer); reject(err); },
    );
  });
}


function isPendingRecord(r: AppraisalPhotoRecord): boolean {
  // "uploading" is also pending: the app may have been killed mid-upload.
  // Older builds could mark Storage complete without reconciling the path into
  // the database arrays consumed by previews/PDFs. Those records must replay.
  // Exception records have exhausted every recovery route; they are handled by
  // support, not by the retry loop.
  if (r.status === "needs_review") return false;
  if (r.status === "archived_legacy") return false;
  return r.status !== "uploaded" || !r.dbSyncedAt;
}

/** Records raised as internal exceptions after all recovery routes failed. */
export function isNeedsReviewRecord(r: AppraisalPhotoRecord): boolean {
  return r.status === "needs_review";
}

/**
 * A record produced by the pre-1.58 Blob pipeline: raw-byte (v2) captures carry
 * `schemaVersion >= 2`. Absent/1 means the record was written by the broken
 * Blob-based implementation shipped up to and including 1.56 / build 156.
 */
function isLegacyBlobRecord(r: AppraisalPhotoRecord): boolean {
  return (r.schemaVersion ?? 1) < PHOTO_SCHEMA_VERSION;
}

/**
 * Legacy Blob → verified v2 raw bytes.
 *
 * Production (1.60, iOS): a legacy v1 record's Blob occasionally materialises
 * once during startup recovery. The old behaviour reset its failure evidence
 * and requeued it WITHOUT durably converting the bytes, so the next read failed
 * again and the record returned to "Syncing" forever (9 → 1 → 9).
 *
 * The migration is therefore monotonic and recovery-first:
 *   materialise → write as v2 ArrayBuffer → INDEPENDENT read-back in a fresh
 *   transaction → only then may the caller reset failure state and requeue.
 * A record already at schema v2 is returned as-is (the current capture/upload
 * path is untouched). Any failure leaves the record — and its terminal
 * evidence — exactly as it was.
 */
async function migrateLegacyRecordToVerifiedRawBytes(
  record: AppraisalPhotoRecord,
): Promise<{ ok: boolean; bytes: Uint8Array | null }> {
  let bytes: Uint8Array | null = null;
  try {
    bytes = await materialisePhotoBytes(record, { timeoutMs: DURABLE_READ_TIMEOUT_MS });
  } catch {
    bytes = null;
  }
  if (!bytes || bytes.byteLength === 0) return { ok: false, bytes: null };

  // Already v2 — nothing to migrate, and nothing about v2 handling changes.
  if (!isLegacyBlobRecord(record)) return { ok: true, bytes };

  const expected = bytes.byteLength;
  try {
    // 1 — persist the actual readable bytes in the existing v2 representation,
    //     preserving every other field (appraisal, slot/category, timestamps).
    await appraisalPhotoDB.upgradeToRawBytes(
      record.appraisalId,
      record.localId,
      bytes,
      record.mimeType || record.blob?.type,
    );
    // 2 — independent read-back: a brand new transaction, freshly deserialised.
    const persisted = await appraisalPhotoDB.getRecord(record.appraisalId, record.localId);
    const persistedLength = readableRawByteLength(persisted);
    if (!persisted || persistedLength === null || persistedLength !== expected) {
      logOfflineEvent("durable_read_failure", {
        appraisalId: record.appraisalId,
        store: "appraisal_photos",
        subject: `${record.category}:${record.localId}`,
        errorKind: "legacy_migration_verification_failed",
        bytes: expected,
        schemaVersion: record.schemaVersion ?? 1,
      });
      return { ok: false, bytes: null };
    }
    if ((persisted.schemaVersion ?? 1) < PHOTO_SCHEMA_VERSION) return { ok: false, bytes: null };

    captureMetric("appraisal.upload", "legacy_blob_migrated_v2", expected, {
      appraisalId: record.appraisalId, localId: record.localId, bytes: expected,
    });
    logOfflineEvent("durable_persist_success", {
      appraisalId: record.appraisalId,
      store: "appraisal_photos",
      subject: `${record.category}:${record.localId}`,
      bytes: expected,
      representation: "raw_bytes",
      reason: "legacy_blob_migration",
    });
    return { ok: true, bytes };
  } catch (err) {
    captureError("appraisal.upload.legacy_migration", err);
    return { ok: false, bytes: null };
  }
}

/** Fresh captures must never sit behind repeatedly failing historic records. */
function compareQueuePriority(a: AppraisalPhotoRecord, b: AppraisalPhotoRecord): number {
  const attemptDifference = (a.attempts ?? 0) - (b.attempts ?? 0);
  if (attemptDifference !== 0) return attemptDifference;
  const schemaDifference = (b.schemaVersion ?? 1) - (a.schemaVersion ?? 1);
  if (schemaDifference !== 0) return schemaDifference;
  return b.createdAt - a.createdAt;
}

function serialiseStorageError(error: unknown): string | null {
  if (!error) return null;
  try {
    const value = error as Record<string, unknown>;
    return JSON.stringify({
      name: value.name ?? null,
      message: value.message ?? String(error),
      status: value.statusCode ?? value.status ?? null,
      error: value.error ?? null,
      body: value.body ?? value.responseBody ?? value.context ?? null,
    });
  } catch {
    return String(error);
  }
}

/**
 * Union-only merge policy for `vehicle_appraisals.additional_photos`.
 *
 * Production case (appraisal 739f9938…, 1.50 iOS): an additional photo was
 * referenced in `additional_photos` but its bytes never reached Storage. That
 * reference is the only record the recovery queue has to reconcile against, so
 * it must survive every later write of the array. The additional-photos step
 * previously OVERWROTE the array with the paths it happened to hold at that
 * moment, which can drop a still-pending reference the queue is about to
 * recover.
 *
 * Rules, matching the reconciliation used by the upload queue:
 *  - existing database references are preserved (union, never replace);
 *  - paths belonging to photos the driver explicitly removed in this session
 *    are dropped, so a retake/delete is still honoured;
 *  - duplicates are collapsed; order is stable (existing first, then new).
 *
 * Nothing here fabricates a path: only paths already referenced or already
 * verified by the queue are written.
 */
export function mergeAdditionalPhotoPaths(
  existingPaths: readonly unknown[] | null | undefined,
  activePaths: readonly string[],
  removedPaths: readonly string[] = [],
): string[] {
  const removed = new Set(removedPaths.filter((p) => typeof p === "string" && p.length > 0));
  const preserved = (Array.isArray(existingPaths) ? existingPaths : [])
    .filter((value): value is string => typeof value === "string" && value.length > 0)
    .filter((value) => !removed.has(value));
  const added = activePaths.filter((value) => typeof value === "string" && value.length > 0 && !removed.has(value));
  return Array.from(new Set([...preserved, ...added]));
}

/**
 * Canonical slug form used to compare a photo category with a stored
 * `part_name`. Both sides are normalised identically so punctuation in part
 * names — "Front Right Wheel (OSF)", "Boot/Trunk", "Bonnet/Hood" — can never
 * prevent a reconciliation match.
 */
export function normalisePartSlug(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

function partNameFromCategory(category: string): string | null {
  if (!category.startsWith("parts:")) return null;
  const slug = category.slice("parts:".length).replace(/:\d+$/, "");
  return slug || null;
}

/**
 * Bounded wait for the part's inspection row. A damage photo is queued the
 * moment it is captured, but its `vehicle_part_inspections` row is only
 * upserted when the inspector leaves that part — so a fast upload can reach
 * reconciliation a few hundred ms before the row is committed. Re-read with a
 * short backoff (~3.75s total) and stop as soon as the row appears. Read
 * errors are thrown immediately (not retried here). Returns null only after
 * every attempt genuinely failed; the caller then raises the existing warning
 * and the queue's normal retry still applies.
 */
export const INSPECTION_ROW_RETRY_DELAYS_MS: readonly number[] = [250, 500, 1000, 2000];

export async function findInspectionRowWithRetry<T extends { part_name: string }>(
  fetchRows: () => Promise<T[]>,
  partSlug: string,
  opts: { delays?: readonly number[]; sleep?: (ms: number) => Promise<void> } = {},
): Promise<T | null> {
  const delays = opts.delays ?? INSPECTION_ROW_RETRY_DELAYS_MS;
  const sleep = opts.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  const target = normalisePartSlug(partSlug);
  for (let attempt = 0; ; attempt++) {
    const rows = await fetchRows();
    const found = rows.find((item) => normalisePartSlug(item.part_name) === target);
    if (found) return found;
    if (attempt >= delays.length) return null;
    await sleep(delays[attempt]);
  }
}


/**
 * Per-appraisal serialisation lock for database reconciliation.
 *
 * `reconcileUploadedPath` is a read-modify-write on a shared array
 * (`vehicle_part_inspections.photo_urls` / `vehicle_appraisals.additional_photos`).
 * The queue drains up to DRAIN_CONCURRENCY photos of the same appraisal at once,
 * so unserialised reconciliation loses updates: several photos verify in Storage
 * but only the last writer's path survives in the database, which is exactly the
 * "photo uploaded but missing from the appraisal and the PDF" failure.
 */
const reconcileLocks = new Map<string, Promise<void>>();

async function reconcileUploadedPath(record: AppraisalPhotoRecord, targetPath: string): Promise<void> {
  const previous = reconcileLocks.get(record.appraisalId) ?? Promise.resolve();
  const run = previous
    .catch(() => {})
    .then(() => reconcileUploadedPathUnsafe(record, targetPath));
  const chained = run.catch(() => {});
  reconcileLocks.set(record.appraisalId, chained);
  try {
    await run;
  } finally {
    // Drop the lock once this link is the tail, so the map cannot grow unbounded.
    if (reconcileLocks.get(record.appraisalId) === chained) reconcileLocks.delete(record.appraisalId);
  }
}

/**
 * The database row for this photo is not visible/writable to the current user
 * (e.g. the job was reassigned to another driver). Never bypassed; the caller
 * holds the record for review instead of retrying it.
 */
export class ReconcilePermissionError extends Error {
  constructor(message: string) { super(message); this.name = "ReconcilePermissionError"; }
}

export function isPermissionShapedError(error: unknown): boolean {
  const code = (error as { code?: unknown } | null)?.code;
  const message = String((error as { message?: unknown } | null)?.message ?? "");
  return code === "PGRST116" || code === "42501" || /row-level security|permission denied/i.test(message);
}

async function reconcileUploadedPathUnsafe(record: AppraisalPhotoRecord, targetPath: string): Promise<void> {
  try {
    await reconcileUploadedPathChecked(record, targetPath);
  } catch (error) {
    if (isPermissionShapedError(error)) throw new ReconcilePermissionError(String((error as { message?: unknown })?.message ?? "permission denied"));
    throw error;
  }
}

async function reconcileUploadedPathChecked(record: AppraisalPhotoRecord, targetPath: string): Promise<void> {
  const { appraisalId, category } = record;



  if (category.startsWith("walkaround:")) {
    const { data: existing, error: readError } = await supabase
      .from("vehicle_part_inspections")
      .select("photo_urls, company_id")
      .eq("appraisal_id", appraisalId)
      .eq("part_name", "Vehicle Photos")
      .maybeSingle();
    if (readError) throw readError;
    // Already referenced: acknowledge without any server write.
    if ((existing?.photo_urls || []).includes(targetPath)) return;

    let companyId = existing?.company_id;
    if (!companyId) {
      const { data: appraisal, error: appraisalError } = await supabase
        .from("vehicle_appraisals")
        .select("company_id")
        .eq("id", appraisalId)
        .single();
      if (appraisalError || !appraisal?.company_id) throw appraisalError || new Error("Appraisal company unavailable for photo reconciliation");
      companyId = appraisal.company_id;
    }

    const paths = Array.from(new Set([...(existing?.photo_urls || []), targetPath]));
    const { error } = await supabase.from("vehicle_part_inspections").upsert({
      appraisal_id: appraisalId,
      part_name: "Vehicle Photos",
      has_damage: false,
      photo_urls: paths,
      company_id: companyId,
    }, { onConflict: "appraisal_id,part_name", ignoreDuplicates: false });
    if (error) throw error;
  } else if (category.startsWith("additional:")) {
    const { data: appraisal, error: readError } = await supabase
      .from("vehicle_appraisals")
      .select("additional_photos")
      .eq("id", appraisalId)
      .single();
    if (readError) throw readError;
    const existingPaths = Array.isArray(appraisal?.additional_photos)
      ? appraisal.additional_photos.filter((value): value is string => typeof value === "string")
      : [];
    if (existingPaths.includes(targetPath)) return;
    const paths = Array.from(new Set([...existingPaths, targetPath]));
    const { error } = await supabase.from("vehicle_appraisals").update({ additional_photos: paths }).eq("id", appraisalId);
    if (error) throw error;
  } else {
    const partSlug = partNameFromCategory(category);
    if (!partSlug) throw new Error(`Unsupported appraisal photo category: ${category}`);
    const inspection = await findInspectionRowWithRetry(async () => {
      const { data: inspections, error: readError } = await supabase
        .from("vehicle_part_inspections")
        .select("id, part_name, photo_urls")
        .eq("appraisal_id", appraisalId);
      if (readError) throw readError;
      return inspections || [];
    }, partSlug);
    if (!inspection) throw new Error(`Inspection row unavailable for photo reconciliation: ${partSlug} (after ${INSPECTION_ROW_RETRY_DELAYS_MS.length + 1} attempts)`);
    if ((inspection.photo_urls || []).includes(targetPath)) return;

    const paths = Array.from(new Set([...(inspection.photo_urls || []), targetPath]));
    const { error } = await supabase.from("vehicle_part_inspections").update({ photo_urls: paths }).eq("id", inspection.id);
    if (error) throw error;
  }

  captureMetric("appraisal.upload", "database_photo_url_updated", 1, {
    appraisalId, localId: record.localId, category, targetPath,
  });
}

/**
 * Recovery/migration: photos captured by older builds have no targetPath and
 * may be marked "failed" forever. Re-queue them so nothing is ever lost and
 * drivers never have to retake a photo.
 */
export async function migrateLegacyRecords(): Promise<number> {
  let migrated = 0;
  try {
    const all = await appraisalPhotoDB.getAllRecordSummaries();
    for (const r of all) {
      if (r.status === "uploaded") continue;
      if (r.status === "archived_legacy") continue;
      if (r.targetPath) continue;
      const label = r.category?.replace(/:/g, "-") || "photo";
      await appraisalPhotoDB.patch(r.appraisalId, r.localId, {
        targetPath: buildAppraisalPhotoPath(r.appraisalId, label, r.createdAt),
        status: "pending_local",
        attempts: 0,
        nextRetryAt: Date.now(),
        lastError: null,
      });
      migrated += 1;
    }
    if (migrated > 0) {
      console.log(`[AppraisalUploadQueue] Recovered ${migrated} legacy photo(s) into the durable queue`);
      captureMetric("appraisal.upload", "recovered", migrated);
    }
  } catch (err) {
    captureError("appraisal.upload.migrate", err);
  }
  return migrated;
}

/**
 * One-time, idempotent archival of exception records created by the broken
 * pre-1.58 Blob photo pipeline (1.56 / build 156 and earlier).
 *
 * A record is archived ONLY when every one of these holds:
 *  - status is `needs_review` (already terminal — never a pending/processing
 *    /uploading/uploaded record),
 *  - it is a legacy Blob record (`schemaVersion` < 2 — a 1.58 capture always
 *    writes schemaVersion 2, so new records can never match),
 *  - its durable bytes cannot be materialised (or are zero-length), and
 *  - no Storage object exists at its path (checked online only; when offline or
 *    the check fails, the record is left exactly as it is).
 *
 * If the bytes DO materialise, the record is returned to the queue instead of
 * archived — nothing recoverable is written off. If the object DOES exist, the
 * record is left for reconciliation. Nothing is deleted: the record, its
 * history, its database reference and any Storage object are all preserved;
 * only its status changes so it is no longer a *current* action item.
 */
export async function archiveBrokenLegacyExceptions(): Promise<number> {
  let archived = 0;
  try {
    const all = await appraisalPhotoDB.getAllRecordSummaries();
    for (const record of all) {
      if (record.status !== "needs_review") continue;   // idempotent: archived records are skipped
      if (!isLegacyBlobRecord(record)) continue;        // never touches 1.58 (v2) records

      // Recoverable bytes? Only a VERIFIED v2 migration counts as recovery;
      // a Blob that merely materialised once must not have its failure
      // evidence reset (it will be unreadable again next session).
      // Bytes are needed only here: re-read this one record in full. Unreadable
      // or gone this pass → change nothing (retried next boot).
      const fullRecord = await appraisalPhotoDB.loadFullRecord(record).catch(() => null);
      if (!fullRecord) continue;
      const migration = await migrateLegacyRecordToVerifiedRawBytes(fullRecord);
      if (migration.ok) {
        await appraisalPhotoDB.patch(record.appraisalId, record.localId, {
          status: "pending_local",
          nextRetryAt: Date.now(),
          durableReadFailures: 0,
          lastError: null,
        });
        continue;
      }

      // Storage object present? Leave it for reconciliation rather than archive.
      const targetPath = record.storagePath || record.targetPath;
      if (targetPath) {
        if (isKnownOffline()) continue;
        const size = await verifyStorageObject(targetPath).catch(() => undefined);
        if (size === undefined) continue; // check failed — prove nothing, change nothing
        if (size !== null) continue;      // object exists — reconciliation handles it
      }

      await appraisalPhotoDB.patch(record.appraisalId, record.localId, {
        status: "archived_legacy",
        archivedAt: Date.now(),
        archivedReason:
          "Created by the pre-1.58 Blob photo pipeline: no recoverable local bytes and no Storage object. Retained for history; not uploadable.",
      });
      await emitTerminalClassification({ ...record, status: "archived_legacy" }, "archived_legacy", {
        evidence: "pre-1.58 Blob record: durable bytes unreadable and Storage conclusively absent",
        storageVerifiedAbsent: Boolean(targetPath),
      });
      archived += 1;
    }
    if (archived > 0) {
      captureMetric("appraisal.upload", "legacy_exceptions_archived", archived);
      console.log(`[AppraisalUploadQueue] Archived ${archived} unrecoverable pre-1.58 exception record(s)`);
    }
  } catch (err) {
    captureError("appraisal.upload.archive_legacy", err);
  }
  return archived;
}

/**
 * Terminal classification of exception records that are genuinely historical —
 * recovery first, archival only as a last resort.
 *
 * A record is considered ONLY when it is already `needs_review` (it has
 * exhausted every retry route and is not current work). Before anything is
 * written off:
 *   1. durable bytes are re-read — if they materialise, the record is requeued;
 *   2. Storage is searched by exact path and across the appraisal prefix — if
 *      an object exists it is reconciled through markUploaded() (union-only,
 *      idempotent) and the record leaves the queue as uploaded.
 *
 * Only when neither yields anything is the record archived, and only when its
 * appraisal is completed (historical) or no longer exists (orphan). An active
 * appraisal is never archived — nor is a record younger than the safety age,
 * so a draft not yet synced to the server can never be mistaken for an orphan.
 * Nothing is deleted: Storage objects, database references, appraisal answers
 * and PDFs are all untouched; only the local record's status changes so dead
 * work stops being presented as current.
 */
const ORPHAN_SAFETY_AGE_MS = 24 * 60 * 60 * 1000;

export async function archiveUnrecoverableExceptions(): Promise<{
  recovered: number;
  archivedHistorical: number;
  archivedOrphan: number;
}> {
  const result = { recovered: 0, archivedHistorical: 0, archivedOrphan: 0 };
  try {
    if (isKnownOffline()) return result;
    const all = await appraisalPhotoDB.getAllRecordSummaries();
    // Records held because the server refused reconciliation are not retried
    // automatically (no repeated errors on every launch/job opening).
    const candidates = all.filter((r) => r.status === "needs_review" && !r.reconcileHeldAt);
    if (candidates.length === 0) return result;

    // Appraisal state, once, for every candidate.
    const ids = Array.from(new Set(candidates.map((r) => r.appraisalId)));
    const { data: appraisals, error } = await supabase
      .from("vehicle_appraisals")
      .select("id, completed_at")
      .in("id", ids);
    if (error) return result; // no authoritative answer — change nothing

    const known = new Map<string, string | null>();
    for (const row of (appraisals || []) as Array<{ id: string; completed_at: string | null }>) {
      known.set(row.id, row.completed_at);
    }

    for (const record of candidates) {
      // 1 — recoverable local bytes? Put it back to work ONLY once the bytes
      // have been durably written as v2 and independently read back; a
      // one-off Blob materialisation is not recovery.
      // Bytes are needed only here: re-read this one record in full. Unreadable
      // or gone this pass → change nothing (retried next boot).
      // A record whose full read THROWS (iOS WebKit "object can not be found":
      // backing file purged) is unrecoverable locally — it is already
      // needs_review after the terminal read-failure threshold — so it goes on
      // to the Storage check below instead of being skipped forever. A record
      // that is genuinely gone (null) is still left alone.
      let bytesUnreadable = false;
      const fullRecord = await appraisalPhotoDB.loadFullRecord(record).catch(() => { bytesUnreadable = true; return null; });
      if (!fullRecord && !bytesUnreadable) continue;
      const migration = fullRecord ? await migrateLegacyRecordToVerifiedRawBytes(fullRecord) : { ok: false as const };
      if (migration.ok) {
        await observeTransition("requeue_recovered_legacy", record, appraisalPhotoDB.patch(record.appraisalId, record.localId, {
          status: "pending_local",
          nextRetryAt: Date.now(),
          durableReadFailures: 0,
          lastError: null,
        }));
        result.recovered += 1;
        continue;
      }

      // 2 — object already in Storage? Reconcile rather than archive.
      // Absence must be AUTHORITATIVE: verifyStorageObject() throws when the
      // check is inconclusive (timeout), and only returns null on a genuine
      // "not found". An inconclusive answer leaves the record exactly as it is.
      const targetPath = record.storagePath || record.targetPath;
      if (targetPath) {
        const exact = await verifyStorageObject(targetPath).catch(() => undefined);
        if (exact === undefined) continue;   // inconclusive — prove nothing, change nothing
        const existing = exact !== null
          ? targetPath
          : await findExistingStorageObject(record, targetPath).catch(() => null);
        if (existing) {
          let permissionDenied = false;
          const reconciled = await markUploaded(record, existing, Date.now(), "recovered_from_storage")
            .then(() => true)
            .catch((err) => { permissionDenied = err instanceof ReconcilePermissionError; return false; });
          if (reconciled) { result.recovered += 1; continue; }
          if (permissionDenied) {
            // Stays needs_review (never uploaded); bytes/metadata kept; no RLS bypass.
            await observeTransition("hold_reconcile_permission_denied", record, appraisalPhotoDB.patch(record.appraisalId, record.localId, {
              reconcileHeldAt: Date.now(),
              lastErrorKind: "reconcile_permission_denied",
              lastError: "Photo is on the server, but this account can no longer update the inspection (job reassigned). Held for review.",
            }));
            continue;
          }
          continue; // reconciliation failed this pass — retry next boot, never archive
        }
      }

      // 3 — nothing recoverable. Terminal only for historical or orphan records.
      const isKnownAppraisal = known.has(record.appraisalId);
      const completedAt = known.get(record.appraisalId) ?? null;
      const olderThanSafetyAge = Date.now() - record.createdAt > ORPHAN_SAFETY_AGE_MS;

      if (isKnownAppraisal && !completedAt) continue;          // active appraisal — protected
      if (!isKnownAppraisal && !olderThanSafetyAge) continue;  // may simply not have synced yet

      await observeTransition("archive_unrecoverable_exception", record, appraisalPhotoDB.patch(record.appraisalId, record.localId, {
        status: "archived_legacy",
        archivedAt: Date.now(),
        archivedReason: isKnownAppraisal
          ? "Historical: the appraisal is completed, the local bytes are unreadable and no Storage object exists. Retained for history; not uploadable."
          : "Orphan: the appraisal no longer exists, the local bytes are unreadable and no Storage object exists. Retained for history; not uploadable.",
      }));
      await emitTerminalClassification({ ...record, status: "archived_legacy" }, "archived_legacy", {
        evidence: isKnownAppraisal
          ? "completed appraisal, unreadable durable bytes and conclusive Storage absence"
          : "absent appraisal, unreadable durable bytes and conclusive Storage absence",
        storageVerifiedAbsent: Boolean(targetPath),
        durableReadFailures: record.durableReadFailures ?? 0,
      });
      if (isKnownAppraisal) result.archivedHistorical += 1; else result.archivedOrphan += 1;
    }

    const archived = result.archivedHistorical + result.archivedOrphan;
    if (archived > 0 || result.recovered > 0) {
      captureMetric("appraisal.upload", "stale_exceptions_resolved", archived, {
        recovered: result.recovered,
        archivedHistorical: result.archivedHistorical,
        archivedOrphan: result.archivedOrphan,
      });
    }
  } catch (err) {
    captureError("appraisal.upload.archive_stale", err);
  }
  return result;
}

/**
 * Terminal classification for recovered photos whose PARENT APPRAISAL NO LONGER
 * EXISTS (1.60.22).
 *
 * Production case (build 162): nine pre-1.58 Blob records were migrated to
 * verified v2 raw bytes; six uploaded normally. The remaining three belong to
 * appraisals whose `vehicle_appraisals` rows no longer exist, so Storage RLS
 * rejects every upload (HTTP 403) and no database association can ever be
 * written. Their bytes are perfectly readable, so the lost-byte terminal rule
 * (5 verified durable-read failures) can never apply and they retry forever
 * while showing as ordinary "Syncing" work.
 *
 * This is deliberately narrow and monotonic. A record is classified ONLY when
 * every one of these holds:
 *  - it is still pending (never an uploaded, needs_review or archived record);
 *  - it is older than the orphan safety age, so an appraisal that has simply
 *    not synced to the server yet can never be mistaken for a deleted one;
 *  - the appraisal lookup SUCCEEDED and authoritatively did not return the row
 *    (any network/database error aborts the whole pass — nothing is written);
 *  - no Storage object exists at its path, and that check was conclusive.
 *
 * An appraisal that exists — completed or active — is never touched here.
 * Nothing is deleted: local bytes, diagnostics, Storage objects and database
 * references are all preserved; only the status changes so the record stops
 * being presented as a current upload. No RLS is bypassed and no appraisal or
 * photo association is fabricated.
 */
export async function archiveOrphanedPendingPhotos(): Promise<number> {
  let archived = 0;
  try {
    if (isKnownOffline()) return 0;
    const all = await appraisalPhotoDB.getAllRecordSummaries();
    const candidates = all.filter(
      (r) => isPendingRecord(r) && Date.now() - r.createdAt > ORPHAN_SAFETY_AGE_MS,
    );
    if (candidates.length === 0) return 0;

    const ids = Array.from(new Set(candidates.map((r) => r.appraisalId)));
    const { data: appraisals, error } = await supabase
      .from("vehicle_appraisals")
      .select("id")
      .in("id", ids);
    if (error) return 0;                       // inconclusive — retry normally
    if (!appraisals) return 0;

    const known = new Set((appraisals as Array<{ id: string }>).map((row) => row.id));

    for (const record of candidates) {
      if (known.has(record.appraisalId)) continue;   // parent exists — untouched

      // 1.0.x — the parent appraisal is authoritatively absent (a successful
      // query, not an error). Whether or not an object exists at the exact
      // target path, reconciliation cannot succeed (no appraisal row to link
      // to) and uploading is refused by RLS, so retrying only generates noise.
      // The record is RETAINED locally (bytes + diagnostics), never marked
      // uploaded and never deleted; the Storage object, if any, is untouched.
      // An inconclusive existence check still changes nothing.
      const targetPath = record.storagePath || record.targetPath;
      let storageObjectPresent = false;
      if (targetPath) {
        const exact = await verifyStorageObject(targetPath).catch(() => undefined);
        if (exact === undefined) continue;     // inconclusive — change nothing
        storageObjectPresent = exact !== null;
      }

      await observeTransition("archive_orphaned_pending", record, appraisalPhotoDB.patch(record.appraisalId, record.localId, {
        status: "archived_legacy",
        archivedAt: Date.now(),
        archivedReason:
          "Orphan: the parent appraisal no longer exists, so this photo cannot be uploaded or associated. Local bytes and diagnostics retained for support; not uploadable.",
      }));
      await emitTerminalClassification({ ...record, status: "archived_legacy" }, "archived_legacy", {
        evidence: storageObjectPresent
          ? "parent appraisal authoritatively absent; Storage object exists at the exact target path and is retained"
          : "parent appraisal authoritatively absent and no Storage object at the target path",
        storageVerifiedAbsent: !storageObjectPresent,
        storageObjectPresent,
        orphan: true,
      });
      archived += 1;
    }

    if (archived > 0) {
      captureMetric("appraisal.upload", "orphaned_pending_archived", archived);
      console.log(`[AppraisalUploadQueue] Archived ${archived} orphaned photo record(s) with no parent appraisal`);
    }
  } catch (err) {
    captureError("appraisal.upload.archive_orphaned_pending", err);
  }
  return archived;
}

/**
 * A pending record with no destination is either (a) a photo mid-capture on an
 * older code path, between its save and its queue step, or (b) a legacy record
 * (older build, app killed before queueing). (a) must be left for the queue
 * step to name; (b) must never be stranded. Records older than this window are
 * given the SAME deterministic legacy destination `migrateLegacyRecords` uses
 * (category + createdAt), written before any upload, so every pass agrees on
 * one path. Retry/status bookkeeping is not touched.
 */
export const LEGACY_DESTINATION_GRACE_MS = 60_000;

async function adoptLegacyRecordsWithoutDestination(all: AppraisalPhotoRecord[]): Promise<void> {
  const now = Date.now();
  for (const r of all) {
    if (r.targetPath || !isPendingRecord(r) || r.status === "deleted") continue;
    if (now - (r.createdAt ?? 0) < LEGACY_DESTINATION_GRACE_MS) continue;
    const label = r.category?.replace(/:/g, "-") || "photo";
    const targetPath = buildAppraisalPhotoPath(r.appraisalId, label, r.createdAt);
    try {
      await appraisalPhotoDB.patch(r.appraisalId, r.localId, { targetPath });
      r.targetPath = targetPath;
    } catch { /* left pending; retried on the next pass */ }
  }
}

async function drain(): Promise<void> {
  // Phase 14.6 — a drain requested while one is in flight must not be dropped:
  // a photo captured mid-drain would otherwise wait for the recovery poll.
  if (draining) { drainRequested = true; return; }
  if (isKnownOffline()) {
    pending = await getPendingUploadCount();
    status = "idle";
    notify();
    return;
  }

  // Stage 1.1 — deletions made while offline still owe the server a reference
  // prune. Drained here so it rides the EXISTING triggers (15s poll, reconnect,
  // resume, foreground, manual flush). It never touches a pending upload, never
  // gates one, and its failure cannot affect the drain below.
  if (hasPhotoReferenceCleanups()) {
    try { await drainPhotoReferenceCleanups(); } catch { /* retained for retry */ }
  }

  draining = true;

  try {
    const all = await appraisalPhotoDB.getAllRecordSummaries();
    drainBackoffMs = 0;
    drainBackoffUntil = 0;
    await adoptLegacyRecordsWithoutDestination(all);
    const queue = all
      .filter(isPendingRecord)
      .filter((r) => !!r.targetPath)
      .filter((r) => (r.nextRetryAt ?? 0) <= Date.now())
      .sort(compareQueuePriority);

    pending = all.filter(isPendingRecord).length;
    status = queue.length > 0 ? "uploading" : "idle";
    captureMetric("appraisal.upload", "pending_count_updated", pending, { pendingCount: pending });
    notify();

    const drainStartedAt = Date.now();
    if (queue.length > 0) {
      logOfflineEvent("queue_replay_started", {
        store: "appraisal_upload_queue",
        count: queue.length,
        pending,
        appraisalId: queue[0]?.appraisalId ?? null,
      });
    }

    // Process one bounded batch, then re-read the durable queue. A fresh photo
    // queued during a slow batch can therefore lead the next batch instead of
    // waiting behind the entire restored backlog.
    const batch = queue.slice(0, DRAIN_CONCURRENCY);
    const worker = async (summary: AppraisalPhotoRecord) => {
        if (isKnownOffline()) return;
        // Only the photo actually being uploaded is read with its bytes, in a
        // fresh transaction. Gone/unreadable → untouched; the next pass retries.
        const record = await appraisalPhotoDB.loadFullRecord(summary);
        if (!record || !isPendingRecord(record)) return;
        await uploadRecord(record);
        pending = await getPendingUploadCount();
        captureMetric("appraisal.upload", "pending_count_updated", pending, {
          pendingCount: pending, appraisalId: record.appraisalId, localId: record.localId,
        });
        notify();
    };
    await Promise.all(batch.map((record) => worker(record)));
    if (queue.length > batch.length) drainRequested = true;

    pending = await getPendingUploadCount();
    status = "idle";
    notify();
    if (queue.length > 0) {
      logOfflineEvent("queue_replay_completed", {
        store: "appraisal_upload_queue",
        count: queue.length,
        remaining: pending,
        durationMs: Date.now() - drainStartedAt,
        appraisalId: queue[0]?.appraisalId ?? null,
      });
    }
  } catch (err) {
    if (isRecoverableDbError(err)) {
      drainBackoffMs = drainBackoffMs ? Math.min(drainBackoffMs * 2, DRAIN_BACKOFF_MAX_MS) : DRAIN_BACKOFF_BASE_MS;
      drainBackoffUntil = Date.now() + drainBackoffMs;
      captureError("appraisal.upload.drain", err, { metadata: { storage_recoverable: true, backoff_ms: drainBackoffMs } });
    } else {
      captureError("appraisal.upload.drain", err);
    }
    status = "idle";
    notify();
  } finally {
    draining = false;
  }
  if (drainRequested) {
    drainRequested = false;
    await drain();
  }
}

/**
 * Read a record's durable bytes, bounded by {@link DURABLE_READ_TIMEOUT_MS}.
 *
 * Two hard rules, both learned from production 1.56:
 *  1. A failure here is a DURABLE STORAGE problem, never "the camera temp file
 *     was purged" — the camera source is not what is being read.
 *  2. It must be bounded. An unbounded read stalled ~60s per broken record and
 *     starved every other photo in the queue.
 *
 * Phase 15.5 (D2): the body must be a Uint8Array. supabase-js wraps ANY Blob
 * body in FormData, which WKWebView serialises as zero bytes.
 */
async function materialiseBytes(record: AppraisalPhotoRecord): Promise<Uint8Array> {
  const startedAt = Date.now();
  try {
    const bytes = await materialisePhotoBytes(record, { timeoutMs: DURABLE_READ_TIMEOUT_MS });
    // Opportunistically upgrade a legacy Blob record now its bytes are known.
    if (record.schemaVersion !== 2) {
      await appraisalPhotoDB.upgradeToRawBytes(record.appraisalId, record.localId, bytes, record.mimeType || record.blob?.type);
    }
    logOfflineEvent("durable_reopen_read_success", {
      ...photoIdentity(record, { bytes: bytes.byteLength, durationMs: Date.now() - startedAt }),
      store: "appraisal_photos",
      subject: `${record.category}:${record.localId}`,
      schemaVersion: record.schemaVersion ?? 1,
    });
    return bytes;
  } catch (error) {
    const pipelineError = error instanceof PhotoPipelineError
      ? error
      : new PhotoPipelineError("durable_storage_read_failed", (error as Error)?.message || String(error), { cause: error });
    logOfflineEvent(pipelineError.timedOut ? "durable_read_timeout" : "durable_read_failure", {
      ...photoIdentity(record, {
        errorKind: pipelineError.kind,
        failureKind: pipelineError.kind,
        durationMs: Date.now() - startedAt,
      }),
      store: "appraisal_photos",
      subject: `${record.category}:${record.localId}`,
      error: pipelineError,
    });
    throw pipelineError;
  }
}


/**
 * Confirm the object really exists in Storage before a photo is called
 * "uploaded". A 200 from the upload API is not proof on a flaky mobile link:
 * the exact object must be observable and have non-zero size.
 *
 * Production incident (1.60, iOS 18.7): verification used a *directory listing*
 * of the appraisal prefix. Under a loaded queue that request repeatedly hit its
 * 20s bound, so `markUploaded()` was never reached for photos whose bytes were
 * demonstrably already in Storage: no association was written, the record never
 * left the pending count, and the very same bytes were uploaded again on the
 * next pass. The object is therefore resolved by its EXACT key first — a single
 * metadata lookup, not an enumeration — and the listing is kept only as a
 * fallback for runtimes/mocks without the metadata API.
 *
 * Returns the byte size, or null when the object is genuinely not there.
 * A timeout still throws: it proves nothing, and callers keep the photo queued.
 */
const EXACT_OBJECT_CHECK_TIMEOUT_MS = 8_000;

/** Size of an exact object from a metadata response, when it is usable. */
function exactObjectSize(data: any): number | null {
  if (!data) return null;
  const candidate = data.size ?? data.contentLength ?? data.metadata?.size;
  return typeof candidate === "number" && candidate > 0 ? candidate : null;
}

function isNotFoundError(error: any): boolean {
  const status = error?.statusCode ?? error?.status;
  if (status === 404 || status === "404") return true;
  return /not found|does not exist|no such (file|key|object)/i.test(error?.message || "");
}

async function verifyStorageObject(path: string): Promise<number | null> {
  const api: any = supabase.storage.from(BUCKET);

  // ── Exact-key lookup (preferred) ──
  if (typeof api.info === "function") {
    try {
      const { data, error } = await withNetworkTimeout<{ data: any; error: any }>(
        "storage_object_info",
        api.info(path),
        EXACT_OBJECT_CHECK_TIMEOUT_MS,
      );
      if (!error) {
        const size = exactObjectSize(data);
        if (size !== null) return size;
        // Responded, but without a usable size — fall through and confirm.
      } else if (isNotFoundError(error)) {
        return null; // authoritative: the exact object is absent
      }
    } catch {
      // Timed out or threw: proves nothing. Fall through to the listing.
    }
  }

  // ── Fallback: prefix listing narrowed to the exact name ──
  const slash = path.lastIndexOf("/");
  const dir = slash >= 0 ? path.slice(0, slash) : "";
  const name = slash >= 0 ? path.slice(slash + 1) : path;
  if (typeof api.list !== "function") return null;
  // Bounded: an unsettled list request must never stall an upload worker.
  // A timeout throws (proves nothing about the object) — callers decide.
  const { data, error } = await withNetworkTimeout<{ data: any[] | null; error: any }>(
    "storage_object_check",
    api.list(dir, { limit: 100, search: name }),
    STORAGE_OP_TIMEOUT_MS,
  );
  // 1.0.x — a listing that ERRORED (e.g. HTTP 544 on iOS) proves nothing about
  // the object. It used to return null, which callers treat as authoritative
  // absence: an uploaded photo was requeued and its (possibly purged) local
  // bytes re-read. It is now inconclusive, like a timeout.
  if (error || !Array.isArray(data)) {
    throw new PhotoPipelineError(
      "storage_verification_failed",
      `Storage existence check inconclusive: ${(error as any)?.message || "no listing returned"}`,
    );
  }
  const match = data.find((o: any) => o?.name === name);
  const size = match?.metadata?.size;
  return typeof size === "number" && size > 0 ? size : null;
}

/**
 * The cloud path this record was previously verified and reconciled under,
 * when it differs from the queued targetPath. Only a path inside the record's
 * own appraisal prefix qualifies.
 */
function recordedCloudPath(record: AppraisalPhotoRecord): string | null {
  const path = record.storagePath;
  if (!path || path === record.targetPath) return null;
  return path.startsWith(`${record.appraisalId}/`) ? path : null;
}

/** Per-record cooldown after a confirmed cloud copy could not be recorded locally. */
const recordedCopyRetryAt = new Map<string, number>();

/**
 * Independent confirmation that an exact object exists: the public-URL HEAD
 * first, then the existing exact lookup. Sequential, never a broad listing.
 * Returns false whenever existence is not positively confirmed.
 */
async function confirmCloudCopy(path: string): Promise<boolean> {
  if (await headPublicPhotoObject(BUCKET, path)) return true;
  const size = await verifyStorageObject(path).catch(() => null);
  return size !== null;
}

/**
 * Pick the stored object that is unambiguously this photo, from a listing of
 * its own appraisal folder (appraisalId/ — scoping by appraisal also scopes
 * job and company). Accepts current "<label>-<ts>.<ext>" names and the legacy
 * walkaround "vehicle-<position>-<ts>.<ext>" names. The label must be followed
 * by "-" or "." so "nsr_corner" never matches "nsr_corner_rear". Zero-byte
 * objects are ignored; two or more distinct matches are ambiguous -> null.
 */
export function matchStoredPhotoName(objects: Array<{ name?: unknown; metadata?: { size?: number } | null }>, category: string | null | undefined): string | null {
  const label = (category || "photo").replace(/[^a-zA-Z0-9._-]+/g, "-").replace(/^-+|-+$/g, "");
  if (!label) return null;
  const labels = [label];
  if (label.startsWith("walkaround-")) labels.push(`vehicle-${label.slice("walkaround-".length)}`);
  const esc = (v: string) => v.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(`^(?:${labels.map(esc).join("|")})(?:-[A-Za-z0-9]+)?\\.(?:jpe?g|png|webp|heic)$`, "i");
  const hits = objects.filter((o) => typeof o?.name === "string" && re.test(o.name) && (o?.metadata?.size ?? 0) > 0);
  return hits.length === 1 ? String(hits[0].name) : null;
}

/**
 * Last-resort recovery before a photo is raised as an exception: the bytes may
 * already be in Storage from an earlier attempt, under the record's target
 * path or under another name within the same appraisal prefix (older builds
 * used a different timestamp suffix). Returns the recovered path, or null.
 */
async function findExistingStorageObject(record: AppraisalPhotoRecord, targetPath: string): Promise<string | null> {
  try {
    if (await verifyStorageObject(targetPath)) return targetPath;
    const api: any = supabase.storage.from(BUCKET);
    if (typeof api.list !== "function") return null;
    const { data } = await withNetworkTimeout<{ data: any[] | null; error: any }>(
      "storage_prefix_scan",
      api.list(record.appraisalId, { limit: 1000 }),
      STORAGE_OP_TIMEOUT_MS,
    );
    if (!Array.isArray(data)) return null;
    const name = matchStoredPhotoName(data, record.category);
    if (!name) return null;
    // Positive confirmation of the exact object before it is adopted.
    const path = `${record.appraisalId}/${name}`;
    return (await verifyStorageObject(path)) !== null ? path : null;
  } catch {
    return null;
  }
}


/**
 * Single success path. A photo only becomes "uploaded" here, and only once the
 * Storage object has been observed and the path reconciled into the database
 * record used by previews and PDFs.
 */
async function markUploaded(
  record: AppraisalPhotoRecord,
  path: string,
  startedAt: number,
  reason: string,
  uploadedBytes?: number,
  options: { uploadConfirmed?: boolean; existenceConfirmed?: boolean } = {},
): Promise<void> {
  const { appraisalId, localId } = record;
  // 1.65 (J-1074) — the post-upload existence check is CONFIRMATION, not the
  // decision. When Storage has already returned a successful upload response,
  // a check that times out or cannot resolve the object must not turn a
  // successful upload into a failure and a second upload of the same bytes.
  // Reconciliation still runs later and requeues the photo if the object is
  // genuinely absent; the durable bytes are untouched either way.
  // 1.0.x — `existenceConfirmed`: the caller has ALREADY positively confirmed
  // the exact object (confirmCloudCopy); same deferral semantics.
  const uploadConfirmed = options.uploadConfirmed === true || options.existenceConfirmed === true;
  let verifiedBytes: number | null = null;
  let verificationDeferred = false;
  try {
    verifiedBytes = await verifyStorageObject(path);
  } catch (error) {
    if (!uploadConfirmed) throw error;
    verificationDeferred = true;
  }
  if (verifiedBytes === null) {
    if (!uploadConfirmed) {
      throw new PhotoPipelineError(
        "storage_verification_failed",
        "Storage upload could not be verified (object not found after upload)",
      );
    }
    verificationDeferred = true;
  }
  if (verificationDeferred) {
    logOfflineEvent("storage_verification_deferred", {
      ...photoIdentity(record, {
        targetPath: path, bytes: uploadedBytes ?? null, reason,
        uploadConfirmed: true, timestamp: new Date().toISOString(),
      }),
      store: "appraisal_upload_queue",
      subject: `${record.category}:${localId}`,
    });
  }
  // ── H4 — Storage-side length verification (1.60.51) ──
  // Uses the size Storage already reports; no object is downloaded. Only
  // applied when the record carries an expected length, so existing records
  // verify exactly as they did before. A mismatch throws BEFORE any
  // storageVerifiedAt / dbSyncedAt / uploaded state is written: the photo stays
  // queued with its durable bytes intact. Nothing is deleted and no
  // replacement path is created.
  // Scope, deliberately narrow: only the fresh-upload path asserts. An
  // adoption/reconciliation path (preflight, startup repair) must keep its
  // existing behaviour — an object that is already in Storage is still
  // adopted, and a size difference there is reported, never blocking.
  const freshUpload = typeof uploadedBytes === "number" && uploadedBytes > 0;
  // Blocking only on TRUNCATION — Storage reporting fewer bytes than were
  // uploaded. A reported size that is equal or larger is recorded, not
  // blocked: object metadata is not a guaranteed exact byte count and a
  // working upload must never be held back by a metadata quirk.
  const truncated = freshUpload && typeof verifiedBytes === "number" && verifiedBytes > 0 && verifiedBytes < uploadedBytes;
  const storageResult: IntegrityVerdict = truncated
    ? verifyStorageLength({ expectedByteLength: uploadedBytes }, verifiedBytes)
    : { ok: true, reason: "no_expectation" };

  const storageVerdict = integrityFailure(storageResult);
  if (!truncated && verifiedBytes !== null) {
    const observationResult = verifyStorageLength(
      freshUpload ? { expectedByteLength: uploadedBytes } : record,
      verifiedBytes,
    );
    const observation = integrityFailure(observationResult);
    if (observation) {
      logOfflineEvent("photo_integrity_length_mismatch", {
        ...photoIdentity(record, {
          stage: "reconciliation_observation",
          blocking: false,
          targetPath: path,
          reason,
          expectedByteLength: observation.expected,
          actualByteLength: observation.actual,
          timestamp: new Date().toISOString(),
        }),
        store: "appraisal_upload_queue",
        subject: `${record.category}:${localId}`,
      });
    }
  }
  if (storageVerdict) {
    logOfflineEvent("photo_integrity_length_mismatch", {
      ...photoIdentity(record, {
        stage: "storage_verification",
        targetPath: path,
        expectedByteLength: storageVerdict.expected,
        actualByteLength: storageVerdict.actual,
        failureKind: storageVerdict.kind,
        sha256: record.sha256 ?? null,
        timestamp: new Date().toISOString(),
      }),
      store: "appraisal_upload_queue",
      subject: `${record.category}:${localId}`,
    });
    throw new PhotoPipelineError(
      storageVerdict.kind,
      `Storage object byte length ${storageVerdict.actual} does not match the durable payload (${storageVerdict.expected})`,
    );
  }

  if (verifiedBytes !== null) {
    captureMetric("appraisal.upload", "storage_verified", verifiedBytes, photoIdentity(record, {
      targetPath: path, bytes: verifiedBytes, expectedByteLength: record.expectedByteLength ?? null,
      sha256: record.sha256 ?? null,
    }));
    logOfflineEvent("storage_verification_success", {
      ...photoIdentity(record, { targetPath: path, bytes: verifiedBytes }),
      store: "appraisal_upload_queue",
      subject: `${record.category}:${localId}`,
    });
  }


  // ── H6 — db_pending is now an explicit, recorded condition ──
  // The Storage object is verified; the database association has not happened
  // yet. Recording `storageVerifiedAt` here makes that window visible instead
  // of implicit. It is NOT a failure, NOT terminal, and changes nothing about
  // queueing: the record is still pending until `dbSyncedAt` is set below by
  // the unchanged reconciliation path, and it is never re-uploaded because of
  // it (the pre-flight finds the existing object and reconciles).
  const verifiedAt = Date.now();
  await observeTransition("mark_storage_verified", record, appraisalPhotoDB.patch(appraisalId, localId, {
    storageVerifiedAt: verifiedAt,
    storageVerifiedPath: path,
  }), { targetPath: path });
  if (!record.dbPendingTelemetryAt) {
    try {
      logOfflineEvent("photo_db_pending", {
        ...photoIdentity(record, { targetPath: path, bytes: verifiedBytes, storageVerifiedAt: verifiedAt, reason }),
        store: "appraisal_photos",
        subject: `${record.category}:${localId}`,
      });
      await appraisalPhotoDB.patch(appraisalId, localId, { dbPendingTelemetryAt: verifiedAt });
    } catch { /* telemetry must never break the pipeline */ }
  }

  // ── Deletion guard (1.63.x) — never resurrect a deleted photo ──
  // The driver may have deleted/replaced this photo while its upload was in
  // flight. The durable record is the authority: if it is gone or tombstoned,
  // or its target no longer matches the object just uploaded, the path must
  // NOT be appended to `photo_urls`. The orphaned object is cleaned up and the
  // database reference pruned, so the deletion outcome is retained.
  // A read failure is NOT evidence of deletion — only a successful read that
  // shows the record gone or tombstoned may cancel reconciliation.
  let cancelled = isPhotoDeleted(appraisalId, localId, path);
  if (!cancelled && typeof appraisalPhotoDB.getRecord === "function") {
    try {
      const current = await appraisalPhotoDB.getRecord(appraisalId, localId);
      cancelled = !!current && (current.status === "deleted" || !!current.deletedAt);
    } catch {
      cancelled = false;
    }
  }
  if (cancelled) {
    logOfflineEvent("photo_upload_discarded_after_delete", {
      ...photoIdentity(record, { targetPath: path, reason: "deleted_during_upload" }),
      store: "appraisal_upload_queue",
      subject: `${record.category}:${localId}`,
    });
    try {
      await prunePhotoReferences(appraisalId, [path]);
    } catch (error) {
      // Stage 1.1: a failed prune here is the same stale-reference hazard as an
      // offline delete — persist it instead of losing it.
      enqueuePhotoReferenceCleanup({ appraisalId, paths: [path], localIds: [localId], error });
    }
    try { await supabase.storage.from(BUCKET).remove([path]); } catch { /* best effort */ }
    try { await appraisalPhotoDB.deletePhoto(appraisalId, localId); } catch { /* best effort */ }
    const cancelledWaiters = uploadWaiters.get(`${appraisalId}:${localId}`);
    uploadWaiters.delete(`${appraisalId}:${localId}`);
    for (const resolve of cancelledWaiters ?? []) resolve(path);
    return;
  }

  await reconcileUploadedPath(record, path);
  await appraisalPhotoDB.patch(appraisalId, localId, {
    status: "uploaded",
    storagePath: path,
    lastError: null,
    lastErrorKind: null,
    durableReadFailures: 0,
    dbSyncedAt: Date.now(),
    storageVerifiedAt: Date.now(),
  });
  const waiterKey = `${appraisalId}:${localId}`;
  const waiters = uploadWaiters.get(waiterKey);
  uploadWaiters.delete(waiterKey);
  for (const resolve of waiters ?? []) resolve(path);
  captureMetric("appraisal.upload", reason === "uploaded" ? "success" : reason, Date.now() - startedAt, photoIdentity(record, {
    targetPath: path, bytes: uploadedBytes ?? verifiedBytes, status: "uploaded",
    durationMs: Date.now() - startedAt,
  }));
  logOfflineEvent("upload_success", {
    ...photoIdentity(record, {
      targetPath: path, bytes: uploadedBytes ?? verifiedBytes, status: "uploaded",
      durationMs: Date.now() - startedAt, reason,
    }),
    store: "appraisal_upload_queue",
    subject: `${record.category}:${localId}`,
  });
}

/** Deterministic lifecycle state of a single captured photo. */
export type PhotoLifecycleState =
  | "uploaded"            // DB yes / local yes / storage yes / verified yes
  | "db_pending"          // storage verified yes / database association not yet written
  | "uploading"
  | "retry_pending"       // DB yes / local yes / storage no  → retry
  | "waiting_for_network"
  | "recovery_required"   // DB yes / local no  / storage no
  | "archived_legacy";    // pre-1.58 Blob-pipeline exception, retained for history

export interface PhotoLifecycleDiagnostics {
  photoId: string;
  appraisalId: string;
  localId: string;
  category: string;
  state: PhotoLifecycleState;
  localBytes: number | null;
  hasLocalBytes: boolean;
  storagePath: string | null;
  storageVerifiedAt: number | null;
  capturedAt: number | null;
  persistedAt: number | null;
  queuedAt: number | null;
  attempts: number;
  firstAttemptAt: number | null;
  lastAttemptAt: number | null;
  lastErrorKind: string | null;
  schemaVersion: number | null;
}

function lifecycleStateOf(record: AppraisalPhotoRecord): PhotoLifecycleState {
  if (record.status === "uploaded" && record.dbSyncedAt) return "uploaded";
  // H6 — the photo is safely in Storage; only the database association is
  // outstanding. Recoverable, not failed, and handled by reconciliation.
  if (record.storageVerifiedAt && !record.dbSyncedAt
      && record.status !== "needs_review" && record.status !== "archived_legacy") {
    return "db_pending";
  }
  if (record.status === "uploading") return "uploading";
  if (record.status === "archived_legacy") return "archived_legacy";
  if (record.status === "needs_review") return "recovery_required";
  if (isKnownOffline()) return "waiting_for_network";
  return "retry_pending";
}

/**
 * Support/diagnostics view: for one appraisal, what is expected, what exists
 * locally, what the database references, what exists in Storage and what has
 * been verified. Read-only — it changes nothing.
 */
export async function getPhotoLifecycleDiagnostics(appraisalId: string): Promise<PhotoLifecycleDiagnostics[]> {
  const records = await appraisalPhotoDB.getPhotosForAppraisal(appraisalId);
  return records.map((record) => ({
    photoId: record.id,
    appraisalId: record.appraisalId,
    localId: record.localId,
    category: record.category,
    state: lifecycleStateOf(record),
    localBytes: record.byteLength ?? record.blob?.size ?? null,
    hasLocalBytes: Boolean(record.bytes || record.blob),
    storagePath: record.storagePath ?? record.targetPath ?? null,
    storageVerifiedAt: record.storageVerifiedAt ?? null,
    capturedAt: record.createdAt ?? null,
    persistedAt: record.persistedAt ?? null,
    queuedAt: record.queuedAt ?? null,
    attempts: record.attempts ?? 0,
    firstAttemptAt: record.firstAttemptAt ?? null,
    lastAttemptAt: record.lastAttemptAt ?? null,
    lastErrorKind: record.lastErrorKind ?? null,
    schemaVersion: record.schemaVersion ?? null,
  }));
}

/**
 * Launch / resume reconciliation against Storage.
 *  - queued but the object already exists  → repair metadata, mark uploaded
 *  - marked uploaded but the object is gone → re-enter the queue (integrity
 *    problem must never stay silently "uploaded")
 * Never deletes local bytes and never raises an exception state.
 */
export async function reconcileAppraisalPhotos(appraisalId?: string): Promise<number> {
  let repaired = 0;
  try {
    const all = appraisalId
      ? await appraisalPhotoDB.getSummariesForAppraisal(appraisalId)
      : await appraisalPhotoDB.getAllRecordSummaries();
    for (const record of all) {
      const targetPath = record.storagePath || record.targetPath;
      if (!targetPath) continue;

      if (record.status === "uploaded") {
        if (isKnownOffline()) continue;
        // `undefined` = the check itself failed/timed out and proves nothing;
        // only a completed check reporting absence may requeue the record.
        const size = await verifyStorageObject(targetPath).catch(() => undefined);
        if (size === undefined) continue;
        if (size === null) {
          // Storage object missing for a record the app believes is uploaded.
          await observeTransition("requeue_missing_storage_object", record, appraisalPhotoDB.patch(record.appraisalId, record.localId, {
            status: "pending_local",
            dbSyncedAt: undefined,
            storageVerifiedAt: undefined,
            storageVerifiedPath: undefined,
            nextRetryAt: Date.now(),
            lastErrorKind: "storage_verification_failed",
            lastError: "Storage object missing for a record marked uploaded",
          }), { targetPath });
          repaired++;
        } else if (!record.dbSyncedAt) {
          // db_pending: Storage is verified, the database association is not.
          // Reconciliation — never a fresh upload — completes it.
          await observeTransition("reconcile_db_pending", record,
            markUploaded(record, targetPath, Date.now(), "reconciled_uploaded"), { targetPath });
          repaired++;
        }
        continue;
      }

      if (isPendingRecord(record) && !isKnownOffline()) {
        const existing = (await verifyStorageObject(targetPath).catch(() => null)) !== null ? targetPath : null;
        if (existing) {
          await observeTransition("reconcile_pending_existing_object", record,
            markUploaded(record, existing, Date.now(), "reconciled_uploaded"), { targetPath: existing });
          repaired++;
        }
      }
    }
  } catch (err) {
    captureError("appraisal.upload.reconcile", err);
  }
  if (repaired > 0) {
    captureMetric("appraisal.upload", "reconciled", repaired, { appraisalId: appraisalId ?? null });
    notify();
  }
  return repaired;
}


/**
 * H3/H4 — verify the upload payload against the durable record's integrity
 * metadata, and opportunistically record that metadata when it is missing.
 *
 * Throws only on a genuine, proven mismatch. It never throws because hashing is
 * unavailable, because the record predates 1.60.51, or because it is a legacy
 * v1 record: an absent expectation is "not verified here", never a failure.
 */
async function assertPayloadIntegrity(
  record: AppraisalPhotoRecord,
  body: Uint8Array,
  targetPath: string,
): Promise<void> {
  // 1.60.51 hotfix — hashing must never sit unbounded on the upload path.
  // The digest is only awaited when the record carries a hash to compare
  // against (the only case where it changes a decision), and even then it is
  // bounded: an unsettled digest yields null = "not verified here".
  let actualHash: string | null = null;
  if (record.sha256) {
    try {
      actualHash = await sha256HexBounded(body);
    } catch {
      actualHash = null;            // hashing must never block an upload
    }
  }


  const verdictResult = verifyPayloadIntegrity(record, { byteLength: body.byteLength, sha256: actualHash });
  const verdict = integrityFailure(verdictResult);
  if (verdict) {
    const event = verdict.kind === "integrity_hash_mismatch"
      ? "photo_integrity_hash_mismatch"
      : "photo_integrity_length_mismatch";
    logOfflineEvent(event, {
      ...photoIdentity(record, {
        stage: "upload_payload",
        targetPath,
        expectedByteLength: record.expectedByteLength ?? null,
        actualByteLength: body.byteLength,
        sha256: record.sha256 ?? null,
        actualSha256: actualHash,
        failureKind: verdict.kind,
        timestamp: new Date().toISOString(),
      }),
      store: "appraisal_upload_queue",
      subject: `${record.category}:${record.localId}`,
    });
    captureMetric("appraisal.upload", event, body.byteLength, photoIdentity(record, {
      expected: verdict.expected, actual: verdict.actual, failureKind: verdict.kind, targetPath,
    }));
    throw new PhotoPipelineError(
      verdict.kind,
      verdict.kind === "integrity_hash_mismatch"
        ? "Upload payload hash does not match the durable photo bytes"
        : `Upload payload is ${verdict.actual} bytes, expected ${verdict.expected}`,
    );
  }

  // Backfill only — never a migration, never a prerequisite. A record that
  // reaches upload with readable bytes but no integrity metadata (captured
  // before 1.60.51, or a migrated legacy record) gets it recorded now.
  //
  // 1.60.51 hotfix: backfill runs OFF the upload path. The upload must never
  // wait on a digest that has nothing to compare against.
  const needsLength = typeof record.expectedByteLength !== "number";
  const needsHash = !record.sha256;
  if (needsLength || needsHash) {
    trackIntegrityBackfill((async () => {
      const hash = needsHash ? await sha256HexBounded(body).catch(() => null) : null;
      await observeTransition("integrity_backfill", record, appraisalPhotoDB.patch(record.appraisalId, record.localId, {
        ...(needsLength ? { expectedByteLength: body.byteLength } : {}),
        ...(hash ? { sha256: hash } : {}),
        integrityBackfilledAt: Date.now(),
      }), { targetPath });
      if (hash) {
        logOfflineEvent("photo_integrity_hash_created", {
          ...photoIdentity(record, {
            stage: "upload_backfill",
            targetPath,
            expectedByteLength: body.byteLength,
            sha256: hash,
            timestamp: new Date().toISOString(),
          }),
          store: "appraisal_photos",
          subject: `${record.category}:${record.localId}`,
        });
      }
    })());
  }
}

/**
 * Integrity backfills run detached from the upload path. They are tracked so
 * callers (and tests) can wait for them to settle; a failure here is inert —
 * the photo is already uploaded and nothing is deleted or failed.
 */
const pendingIntegrityBackfills = new Set<Promise<void>>();

function trackIntegrityBackfill(task: Promise<void>): void {
  const tracked = task.catch(() => undefined).then(() => undefined);
  pendingIntegrityBackfills.add(tracked);
  void tracked.finally(() => pendingIntegrityBackfills.delete(tracked));
}

export async function awaitIntegrityBackfills(): Promise<void> {
  while (pendingIntegrityBackfills.size > 0) {
    await Promise.all([...pendingIntegrityBackfills]);
  }
}

async function uploadRecord(record: AppraisalPhotoRecord): Promise<void> {

  const { appraisalId, localId } = record;
  // 1.76.x race fix — never invent a destination here. A record without one is
  // left untouched (not uploaded, not failed, not deleted); drain() adopts
  // genuine legacy records deterministically once they are past the capture
  // window, and new captures always carry their destination from savePhoto.
  const targetPath = record.targetPath;
  if (!targetPath) return;
  const attempts = record.attempts ?? 0;
  const startedAt = Date.now();

  const mimeType = record.mimeType || record.blob?.type || "image/jpeg";
  const knownBytes = record.byteLength ?? record.blob?.size ?? 0;
  captureMetric("appraisal.upload", "start", 0, photoIdentity(record, {
    attempts, bytes: knownBytes, mimeType, targetPath,
  }));
  logOfflineEvent("upload_start", {
    ...photoIdentity(record, { attempts, bytes: knownBytes, mimeType, targetPath }),
    store: "appraisal_upload_queue",
    subject: `${record.category}:${localId}`,
  });
  await observeTransition("mark_uploading", record, appraisalPhotoDB.patch(appraisalId, localId, {
    status: "uploading",
    targetPath,
    firstAttemptAt: record.firstAttemptAt ?? startedAt,
    lastAttemptAt: startedAt,
  }), { targetPath });

  // ── Idempotency pre-flight ──
  // A previous attempt (or an attempt whose response never reached this
  // device) may already have landed the object. Uploading again would be
  // wasteful and, on a legacy path, could duplicate the object.
  // Exact path only — the loose appraisal-prefix scan is a last-resort recovery
  // for unreadable bytes and must never match a *different* photo here.
  // `undefined` = the check was inconclusive (timeout / listing error) and
  // proves nothing; `null` = authoritative absence; a number = present.
  let alreadyThere = await verifyStorageObject(targetPath).catch(() => undefined);
  // 1.82 — legacy queue protection (production 2026-10-22, Android 1.80). A
  // record queued by 1.80/1.81 carries no stage-1 marker. When the metadata
  // checks above are INCONCLUSIVE, the old code fell through to an upsert
  // re-upload — an UPDATE that Storage RLS denies to drivers when the object
  // already exists. One independent public-URL HEAD closes that gap: a
  // positive answer is treated exactly like the numeric case below, so the
  // record is reconciled/attach-only and no overwrite is attempted. A
  // negative or failed HEAD proves nothing (unchecked = stay inconclusive),
  // so a genuinely missing photo still uploads normally.
  let presenceConfirmedByHead = false;
  if (alreadyThere === undefined && !record.storageVerifiedPath) {
    if (await headPublicPhotoObject(BUCKET, targetPath)) {
      alreadyThere = record.byteLength && record.byteLength > 0 ? record.byteLength : 1;
      presenceConfirmedByHead = true;
    }
  }
  // 1.81.x — stage separation. Storage upload and database attachment are
  // independently recoverable. Once the exact object is known to be in Storage
  // (observed now, or durably recorded by an earlier attempt), a failed
  // attachment (e.g. the inspection row is not created yet) retries the
  // ATTACHMENT only. Re-uploading would be an overwrite (upsert → UPDATE),
  // which Storage RLS correctly denies to drivers (403 "new row violates
  // row-level security policy").
  const stageOneRecorded = !!record.storageVerifiedAt && record.storageVerifiedPath === targetPath;
  let attachOnlyError: unknown = null;
  let attachOnly = false;
  if (typeof alreadyThere === "number") {
    let preflightError: unknown = null;
    // presenceConfirmedByHead: the exact object was positively confirmed by
    // the public-URL HEAD, so the internal re-verification (which uses the
    // same metadata checks that just failed) is deferred, never blocking.
    const reconciled = await markUploaded(record, targetPath, startedAt, "preflight_reconciled", undefined,
      presenceConfirmedByHead ? { existenceConfirmed: true } : undefined)
      .then(() => true).catch((err) => { preflightError = err; return false; });
    if (reconciled) return;
    // 1.0.x — the exact object is already in Storage and only the LOCAL state
    // write failed on a broken IndexedDB. Re-reading the bytes and uploading
    // again cannot help: leave the record untouched for a later pass.
    if (isRecoverableDbError(preflightError)) return;
    attachOnly = true;
    attachOnlyError = preflightError ?? new Error("Photo attachment failed after Storage verification");
  } else if (alreadyThere === undefined && stageOneRecorded) {
    attachOnly = true;
  } else if (alreadyThere === null && record.storageVerifiedPath) {
    // Authoritatively absent: the earlier verification no longer holds, so the
    // normal upload path below must run. Clear the stale stage-1 marker.
    try {
      await appraisalPhotoDB.patch(appraisalId, localId, { storageVerifiedPath: undefined, storageVerifiedAt: undefined });
    } catch { /* best effort — the upload below still runs */ }
  }

  // ── Recorded cloud copy (1.0.x) ──
  // A record can carry a storagePath that differs from its targetPath: it was
  // verified and reconciled under that path by an earlier build (legacy
  // "vehicle-<slot>-<ts>" naming), and later given a different deterministic
  // targetPath by legacy migration. If that recorded object is independently
  // confirmed present, it IS this photo's cloud copy: reconcile it and stop —
  // never re-read local bytes that may be permanently gone (iOS NotFoundError)
  // and never upload a duplicate. Unconfirmed → the unchanged path below runs,
  // so a genuinely missing photo still fails, retries and is reported.
  const recordedPath = recordedCloudPath(record);
  if (recordedPath) {
    const cooldownKey = `${appraisalId}:${localId}`;
    if ((recordedCopyRetryAt.get(cooldownKey) ?? 0) > Date.now()) return;
    if (await confirmCloudCopy(recordedPath)) {
      const reconciled = await markUploaded(record, recordedPath, startedAt, "reconciled_recorded_cloud_copy", undefined, { existenceConfirmed: true })
        .then(() => true).catch(() => false);
      captureMetric("appraisal.upload", "recorded_cloud_copy_adopted", 1, photoIdentity(record, {
        targetPath: recordedPath, queuedTargetPath: targetPath, reconciled,
        legacyRecord: isLegacyBlobRecord(record),
      }));
      if (reconciled) { recordedCopyRetryAt.delete(cooldownKey); return; }
      // Cloud copy confirmed; only the local state write failed. Re-reading the
      // bytes cannot help — park this record briefly instead of hot-looping.
      recordedCopyRetryAt.set(cooldownKey, Date.now() + MAX_BACKOFF_MS);
      return;
    }
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), UPLOAD_TIMEOUT_MS);
  try {
    if (attachOnly) {
      // Attachment-only retry: no bytes are read and no Storage write occurs.
      // Failures fall through to the unchanged backoff/retry handling below.
      logOfflineEvent("photo_attach_retry", {
        ...photoIdentity(record, { targetPath, reason: attachOnlyError ? "object_present" : "stage_one_recorded" }),
        store: "appraisal_upload_queue",
        subject: `${record.category}:${localId}`,
      });
      if (attachOnlyError) throw attachOnlyError;
      await markUploaded(record, targetPath, startedAt, "attach_retry_storage_verified", undefined, { existenceConfirmed: true });
      return;
    }
    // ── Pre-flight validation: never issue a Storage request without a
    //    deterministic path, a MIME type and real bytes. ──
    if (!targetPath) throw new Error("Storage path missing for queued photo");
    if (!mimeType) throw new Error("MIME type missing for queued photo");

    const contentType = mimeType;
    // The upload payload is created here, at upload time, from the durable
    // bytes — never from a Blob captured in an earlier session.
    const body = await materialiseBytes(record);
    if (body.byteLength === 0) {
      throw new PhotoPipelineError("durable_storage_corrupt", "Local photo data is unreadable (0 bytes)");
    }
    // ── H3/H4 — payload integrity gate (1.60.51) ──
    // The upload payload must be the bytes the durable record recorded. A
    // mismatch is RECOVERABLE: nothing is deleted, the durable bytes stay put,
    // and the existing retry path handles it. Records without integrity
    // metadata (pre-1.60.51 and legacy v1) are unaffected — they are
    // opportunistically backfilled below instead.
    await assertPayloadIntegrity(record, body, targetPath);

    captureMetric("appraisal.upload", "bytes_uploading", body.byteLength, photoIdentity(record, {
      bytes: body.byteLength, mimeType, targetPath, uploadMethod: "binary-raw",
    }));
    logOfflineEvent("bytes_uploading", {
      ...photoIdentity(record, { bytes: body.byteLength, targetPath }),
      store: "appraisal_upload_queue",
      subject: `${record.category}:${localId}`,
    });

    // NOTE (Phase 15.6): `body` MUST be a Uint8Array. storage-js only wraps
    // Blob/FormData bodies in multipart FormData — which WKWebView serialises
    // as an empty part — while a binary view is sent as the raw request body
    // with an explicit content-type header.
    // Bounded twice over: the AbortController covers fetch implementations that
    // honour `signal`, the race covers WKWebView requests that never settle.
    const { data: storageData, error } = await withNetworkTimeout(
      "storage_upload",
      supabase.storage
        .from(BUCKET)
        .upload(targetPath, body, {
          contentType,
          upsert: true,
          // supabase-js forwards unknown options to fetch init
          ...( { signal: controller.signal } as Record<string, unknown> ),
        }),
      UPLOAD_TIMEOUT_MS + 5_000,
    );

    if (error) {
      const status = (error as any)?.statusCode ?? (error as any)?.status;
      // 1.63 — an RLS/403 rejection is a permanent, actionable DATA problem
      // (e.g. the appraisal has no company_id), never a transient fault.
      const kindForError: PhotoFailureKind = isStorageAuthorizationDenied({ ...(error as any), status, message: error.message })
        ? "storage_authorization_denied"
        : /network|fetch|timeout|abort/i.test(error.message || "")
          ? "network_upload_failed"
          : "storage_upload_failed";
      const e: any = new PhotoPipelineError(kindForError, error.message);
      e.httpStatus = status;
      e.responseBody = serialiseStorageError(error);
      captureMetric("appraisal.upload", "storage_response", Date.now() - startedAt, photoIdentity(record, {
        httpStatus: status ?? null, responseBody: e.responseBody, targetPath,
        durationMs: Date.now() - startedAt, success: false,
      }));
      throw e;
    }

    captureMetric("appraisal.upload", "storage_response", Date.now() - startedAt, photoIdentity(record, {
      httpStatus: 200, responseBody: serialiseStorageError(storageData), targetPath,
      durationMs: Date.now() - startedAt, success: true,
    }));
    logOfflineEvent("storage_response", {
      ...photoIdentity(record, { targetPath, httpStatus: 200, durationMs: Date.now() - startedAt }),
      store: "appraisal_upload_queue",
      subject: `${record.category}:${localId}`,
    });
    // Cloud verification gate lives in markUploaded(): the photo is only
    // "uploaded" once the object is observed in Storage. A missing object is a
    // normal retryable failure — the durable local copy is untouched.

    // Only when the Storage object is verified and the path reconciled is the
    // record released. The durable local bytes are retained until this point.
    // 1.65 — the Storage upload response is the success signal. The existence
    // check inside markUploaded() is confirmation only and may be deferred to
    // background reconciliation when it cannot complete.
    await markUploaded(record, targetPath, startedAt, "uploaded", body.byteLength, { uploadConfirmed: true });


  } catch (err: any) {
    const aborted = controller.signal.aborted || /abort/i.test(err?.message || "");
    const message = aborted ? `Upload timed out after ${UPLOAD_TIMEOUT_MS / 1000}s` : (err?.message || String(err));
    const nextAttempts = attempts + 1;

    // ── Failure classification ──
    // A failure to read the durable bytes is a DURABLE STORAGE failure. It is
    // never attributed to the camera source and never terminal on its own.
    const kind: PhotoFailureKind = err instanceof PhotoPipelineError
      ? err.kind
      : aborted ? "network_upload_failed" : "storage_upload_failed";
    const durableReadProblem = kind === "durable_storage_read_failed" || kind === "durable_storage_corrupt";
    // The durable-read failure tally is monotonic: an unrelated failure (a
    // network timeout, an auth blip) in between must not reset it, or a record
    // whose bytes are gone can alternate failure kinds and retry forever
    // without ever reaching the terminal recovery policy below. It is cleared
    // only by success — markUploaded() releases the record from the queue.
    const durableReadFailures = durableReadProblem
      ? (record.durableReadFailures ?? 0) + 1
      : (record.durableReadFailures ?? 0);

    // ── Recovery before any retry or terminal classification ──
    // A failure here does not prove the object is absent. A timeout or dropped
    // response is the classic case: the server may well have accepted the
    // upload. Unreadable local bytes are the other case — an earlier attempt
    // may already have landed the object, here or under a legacy name in the
    // same appraisal prefix. Always look before retrying or giving up.
    // An integrity mismatch is never "recovered" by adopting the very object
    // that failed verification — it simply retries through the normal path.
    const integrityProblem = kind === "integrity_length_mismatch" || kind === "integrity_hash_mismatch";
    const recoveredPath = integrityProblem
      ? null
      : durableReadProblem
        ? await findExistingStorageObject(record, targetPath).catch(() => null)
        : ((await verifyStorageObject(targetPath).catch(() => null)) !== null ? targetPath : null);
    if (recoveredPath) {
      const reconciled = await markUploaded(record, recoveredPath, startedAt, "recovered_from_storage")
        .then(() => true)
        .catch(() => false);
      if (reconciled) return;
    }

    // ── Terminal recovery policy ──
    // An internal support exception is only raised after repeated, verified
    // durable-read failures with no object anywhere in Storage. Everything
    // else stays queued and retryable; the record and its database reference
    // are always preserved and the driver is never asked to retake a photo.
    const terminal = durableReadProblem
      && durableReadFailures >= DURABLE_READ_TERMINAL_ATTEMPTS;
    // Bounded exponential backoff with jitter — retries never form a tight
    // loop and a fleet of devices never retries in lockstep.
    const base = Math.min(Math.pow(2, nextAttempts) * 5000, MAX_BACKOFF_MS);
    // A permanent authorisation/RLS rejection cannot succeed on the next
    // network attempt: it is surfaced as an actionable failure and parked at
    // the maximum backoff instead of hot-looping. NOTHING is discarded — the
    // durable bytes, the record and its references stay intact and the item
    // remains eligible for retry the moment the data problem is corrected.
    const authorizationDenied = kind === "storage_authorization_denied";
    const backoff = authorizationDenied
      ? MAX_BACKOFF_MS
      : Math.round(base * (0.75 + Math.random() * 0.5));

    if (authorizationDenied) {
      logOfflineEvent("photo_upload_authorization_denied", {
        ...photoIdentity(record, { errorKind: kind, targetPath, httpStatus: err?.httpStatus ?? 403 }),
        store: "appraisal_upload_queue",
        subject: `${record.category}:${localId}`,
        error: message,
      });
      captureMetric("appraisal.upload", "authorization_denied", 1, photoIdentity(record, {
        errorKind: kind, targetPath, httpStatus: err?.httpStatus ?? 403,
        actionable: true, permanent: true,
      }));
      // 1.82 — legacy queue protection. A denied OVERWRITE usually means the
      // object already landed on an earlier attempt (upsert → UPDATE refused
      // by Storage RLS). The 403 alone does NOT prove that — an INSERT the
      // policy rejects (e.g. 1.63, appraisal without company_id) fails
      // identically — so existence is confirmed independently. Only a
      // positive confirmation records the stage-1 marker, converting later
      // retries into attachment-only attempts. An absent or inconclusive
      // confirmation changes nothing: no marker, no deletion, the record and
      // its durable bytes keep the normal retry path untouched.
      if (!record.storageVerifiedPath) {
        const deniedOverwriteConfirmed = await confirmCloudCopy(targetPath).catch(() => false);
        if (deniedOverwriteConfirmed) {
          logOfflineEvent("photo_attach_retry", {
            ...photoIdentity(record, { targetPath, reason: "denied_overwrite_confirmed" }),
            store: "appraisal_upload_queue",
            subject: `${record.category}:${localId}`,
          });
          await appraisalPhotoDB.patch(appraisalId, localId, {
            storageVerifiedPath: targetPath,
            storageVerifiedAt: startedAt,
          }).catch(() => undefined);
        }
      }
    }

    // Auth loss is transient — refresh and retry sooner, never discard.
    if (!authorizationDenied && /jwt|401|unauthor/i.test(message)) {
      try { await supabase.auth.refreshSession(); } catch { /* offline */ }
    }

    await observeTransition("record_failure", record, appraisalPhotoDB.patch(appraisalId, localId, {
      status: terminal ? "needs_review" : "failed",
      attempts: nextAttempts,
      nextRetryAt: Date.now() + backoff,
      lastError: message,
      lastErrorKind: kind,
      durableReadFailures,
      targetPath,
    }), { terminal, errorKind: kind });

    if (terminal) {
      // Observes the classification the existing policy above has ALREADY made.
      await emitTerminalClassification({ ...record, status: "needs_review", durableReadFailures, lastErrorKind: kind, targetPath }, "needs_review", {
        failureKind: kind,
        durableReadFailures,
        storageVerifiedAbsent: true,
        evidence: "durable read failures reached the existing terminal threshold and no Storage object was found under the appraisal prefix",
        lastError: message,
      });
      logOfflineEvent("photo_write_failure", {
        appraisalId,
        store: "appraisal_photos",
        subject: `${record.category}:${localId}`,
        stage: "upload_read",
        errorKind: kind,
        durableReadFailures,
        bytes: knownBytes,
        error: message,
      });
    }

    captureMetric("appraisal.upload", terminal ? "local_data_lost" : aborted ? "timeout" : "retry", Date.now() - startedAt, photoIdentity(record, {
      attempts: nextAttempts, error: message, localDataLost: terminal,
      errorKind: kind, failureKind: kind, durableReadFailures, targetPath,
      bytes: knownBytes, mimeType, durationMs: Date.now() - startedAt,
      uploadMethod: "binary-raw", httpStatus: err?.httpStatus ?? null,
      responseBody: err?.responseBody ?? serialiseStorageError(err),
    }));

    captureError("appraisal.upload", new Error(message), {
      severity: nextAttempts >= 5 ? "error" : "warn",
      metadata: {
        appraisalId, localId, attempts: nextAttempts, targetPath, errorKind: kind,
        httpStatus: err?.httpStatus ?? null,
        responseBody: err?.responseBody ?? serialiseStorageError(err),
      },
    });
  } finally {
    clearTimeout(timer);
  }
}
